// The words of the inspector (F14): the Inspect toggle, outlines, the nesting picker, the
// mention chips, details, the property sheet and the phone sheet.
//
// Namespace `inspect`; owner: DES2 (ui/workspace/inspect). Labels of the things selected come
// from the build manifest or the artifact model; these are only the chrome around them.
// Placeholders: {label}, {n}, {limit}, {before}, {after}, {value}, {channel}, {subject}, {version}. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  fieldContent: 'Content',
  inspectNodeParagraph: 'Paragraph',
  inspectNodeHeading: 'Heading',
  inspectNodeList: 'List',
  viewOnly: 'You can view this, but you can’t change it.',
  undoFailed: 'Couldn’t undo. Your change is still saved.',
  undoConflict: 'This changed after your edit, so it can’t be undone here.',

  toggle: 'Inspect',
  hintTouch: 'Tap any part to select it.',
  addAnother: 'Add another',
  element: 'Element',
  region: 'Region',
  pickerLabel: 'Choose what to select',
  selectParent: 'Select parent',
  mention: 'Mention',
  remove: 'Remove {label}',
  selectedCount: '{n} selected',
  limit: 'You can mention up to {limit} parts at once. Remove one to add another.',
  stale: 'Changed. Select it again.',
  revoked: 'No longer available',
  expired: 'Expired. Select it again.',
  details: 'Details',
  hideDetails: 'Hide details',
  source: 'Source',
  sourceHidden: 'Hidden for your access',
  sourceUnmapped: 'Not mapped to source',
  data: 'Data',
  dataUnmapped: 'Data source not mapped',
  picture: 'Picture',
  pictureReplay: 'From a fresh render of this version',
  pending: 'Loading…',
  targetsLabel: 'Parts you can point at',
  text: 'Text',
  colour: 'Colour',
  spacing: 'Spacing',
  numberFormat: 'Number format',
  mixed: 'Mixed',
  apply: 'Apply',
  cancel: 'Cancel',
  applying: 'Saving…',
  saved: 'Saved to draft',
  undo: 'Undo',
  change: '{before} → {after}',
  refused: 'This can’t be changed directly here.',
  handoff: 'Change this in chat',
  staleApply: 'It changed while you were editing. Select it again.',
  failed: 'Couldn’t save. The previous version is unchanged.',
  saveUncertain: 'This may have saved. We couldn’t confirm it yet.',
  checkAgain: 'Check again',
  hintBar: 'Click a part to mention it · Esc to leave',
  where: 'Where',
  shows: 'Shows',
  changesHere: 'Changes here',
  showSource: 'Show where it’s defined',
  hideSource: 'Hide where it’s defined',
  cropUnavailable: 'Crop unavailable for this view',
  fromData: 'Comes from data, so it can’t be typed over',
  noModel: 'No model is used. Saves a new draft.',
  savedVersion: 'Saved to draft {version}',
  checkBeforePublish: 'Check again before publishing',
  staleSubject: '{subject} changed. Select again.',
  staleGeneric: 'It changed. Select again.',
  shownAs: 'Shown as',
  shownNumber: 'Number',
  shownPercent: 'Percent',
  decimals: 'Decimals',
  units: 'Units',
  unitKr: 'kr',
  unitThousandKr: 'thousand kr',
  nowSuffix: '{value} now',
  selectedToggle: '{n} selected',
  grouping: 'Grouping',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Select it again to change it',
  colourInk: 'Ink',
  colourMuted: 'Muted',
  colourBrand: 'Accent',
  colourSuccess: 'Success',
  colourWarning: 'Warning',
  colourDanger: 'Danger',
  // C2 presentation controls (ARCH-C2-PRESENTATION, DES1 layout 2026-10-02).
  margin: 'Margin',
  padding: 'Padding',
  gap: 'Gap',
  rowGap: 'Row gap',
  columnGap: 'Column gap',
  sideTop: 'Top',
  sideRight: 'Right',
  sideBottom: 'Bottom',
  sideLeft: 'Left',
  sameOnAllSides: 'Same on all sides',
  custom: 'Custom…',
  customPx: 'Size in px',
  smaller: 'Smaller',
  larger: 'Larger',
  pxValue: '{n} px',
  defaultValue: 'Default ({value})',
  channelText: 'Text',
  channelBackground: 'Background',
  channelBorder: 'Border',
  channelFill: 'Fill',
  channelStroke: 'Stroke',
  chooseColour: 'Choose {channel} colour',
  reset: 'Reset',
  colourPaper: 'Paper',
  colourSurface: 'Surface',
  colourSoft: 'Soft',
  colourLine: 'Line',
  colourLineStrong: 'Strong line',
  colourSuccessBg: 'Success background',
  colourWarningBg: 'Warning background',
  colourDangerBg: 'Danger background',
  colourSeries: 'Series {n}',
  formatUnsupported: 'This number format can’t be changed here.',
  colourNone: 'No colour',
  formatScalarNative: 'original format',
  imageFit: 'Fit',
  imageRatio: 'Ratio',
  fitContain: 'Contain',
  fitCover: 'Cover',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'This image can’t be cropped.',
  imageNotReady: 'Fit can change once the image is ready.',
};

export type InspectCopyKey = keyof typeof en;

const sv: Record<InspectCopyKey, string> = {
  fieldContent: 'Innehåll',
  inspectNodeParagraph: 'Stycke',
  inspectNodeHeading: 'Rubrik',
  inspectNodeList: 'Lista',
  viewOnly: 'Du kan se det här, men inte ändra det.',
  undoFailed: 'Det gick inte att ångra. Din ändring är fortfarande sparad.',
  undoConflict: 'Det här har ändrats efter din ändring, så den kan inte ångras här.',

  toggle: 'Inspektera',
  hintTouch: 'Tryck på en del för att markera den.',
  addAnother: 'Lägg till en till',
  element: 'Element',
  region: 'Område',
  pickerLabel: 'Välj vad som ska markeras',
  selectParent: 'Markera överordnad',
  mention: 'Nämn',
  remove: 'Ta bort {label}',
  selectedCount: '{n} markerade',
  limit: 'Du kan nämna upp till {limit} delar åt gången. Ta bort en för att lägga till en till.',
  stale: 'Ändrad. Markera den igen.',
  revoked: 'Inte längre tillgänglig',
  expired: 'Har gått ut. Markera den igen.',
  details: 'Detaljer',
  hideDetails: 'Dölj detaljer',
  source: 'Källa',
  sourceHidden: 'Dold för din åtkomst',
  sourceUnmapped: 'Inte kopplad till källkod',
  data: 'Data',
  dataUnmapped: 'Datakällan är inte kopplad',
  picture: 'Bild',
  pictureReplay: 'Från en ny rendering av den här versionen',
  pending: 'Laddar…',
  targetsLabel: 'Delar du kan peka på',
  text: 'Text',
  colour: 'Färg',
  spacing: 'Avstånd',
  numberFormat: 'Talformat',
  mixed: 'Blandat',
  apply: 'Använd',
  cancel: 'Avbryt',
  applying: 'Sparar…',
  saved: 'Sparat i utkastet',
  undo: 'Ångra',
  change: '{before} → {after}',
  refused: 'Det här går inte att ändra direkt här.',
  handoff: 'Ändra i chatten',
  staleApply: 'Den ändrades medan du redigerade. Markera den igen.',
  failed: 'Kunde inte spara. Den tidigare versionen är oförändrad.',
  saveUncertain: 'Det här kan ha sparats. Vi kunde inte bekräfta det än.',
  checkAgain: 'Kontrollera igen',
  hintBar: 'Klicka på en del för att nämna den · Esc för att lämna',
  where: 'Var',
  shows: 'Visar',
  changesHere: 'Ändras här',
  showSource: 'Visa var den definieras',
  hideSource: 'Dölj var den definieras',
  cropUnavailable: 'Ingen bild för den här vyn',
  fromData: 'Kommer från data, så den kan inte skrivas över',
  noModel: 'Ingen modell används. Sparar ett nytt utkast.',
  savedVersion: 'Sparat i utkast {version}',
  checkBeforePublish: 'Kontrollera igen innan du publicerar',
  staleSubject: '{subject} ändrades. Markera igen.',
  staleGeneric: 'Den ändrades. Markera igen.',
  shownAs: 'Visas som',
  shownNumber: 'Tal',
  shownPercent: 'Procent',
  decimals: 'Decimaler',
  units: 'Enhet',
  unitKr: 'kr',
  unitThousandKr: 'tusen kr',
  nowSuffix: '{value} nu',
  selectedToggle: '{n} markerade',
  grouping: 'Gruppering',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Markera den igen för att ändra den',
  colourInk: 'Bläck',
  colourMuted: 'Dämpad',
  colourBrand: 'Accent',
  colourSuccess: 'Lyckat',
  colourWarning: 'Varning',
  colourDanger: 'Fara',
  margin: 'Marginal',
  padding: 'Utfyllnad',
  gap: 'Mellanrum',
  rowGap: 'Radavstånd',
  columnGap: 'Kolumnavstånd',
  sideTop: 'Överst',
  sideRight: 'Höger',
  sideBottom: 'Nederst',
  sideLeft: 'Vänster',
  sameOnAllSides: 'Samma på alla sidor',
  custom: 'Anpassad…',
  customPx: 'Storlek i px',
  smaller: 'Mindre',
  larger: 'Större',
  pxValue: '{n} px',
  defaultValue: 'Standard ({value})',
  channelText: 'Text',
  channelBackground: 'Bakgrund',
  channelBorder: 'Kant',
  channelFill: 'Fyllning',
  channelStroke: 'Linje',
  chooseColour: 'Välj färg för {channel}',
  reset: 'Återställ',
  colourPaper: 'Papper',
  colourSurface: 'Yta',
  colourSoft: 'Mjuk',
  colourLine: 'Linje',
  colourLineStrong: 'Tydlig linje',
  colourSuccessBg: 'Bakgrund lyckat',
  colourWarningBg: 'Bakgrund varning',
  colourDangerBg: 'Bakgrund fara',
  colourSeries: 'Serie {n}',
  formatUnsupported: 'Det här talformatet kan inte ändras här.',
  colourNone: 'Ingen färg',
  formatScalarNative: 'ursprungligt format',
  imageFit: 'Anpassning',
  imageRatio: 'Format',
  fitContain: 'Rymma',
  fitCover: 'Fylla',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Den här bilden kan inte beskäras.',
  imageNotReady: 'Anpassningen kan ändras när bilden är klar.',
};

const bg: Record<InspectCopyKey, string> = {
  fieldContent: 'Съдържание',
  inspectNodeParagraph: 'Абзац',
  inspectNodeHeading: 'Заглавие',
  inspectNodeList: 'Списък',
  viewOnly: 'Можете да преглеждате това, но не и да го променяте.',
  undoFailed: 'Отмяната не беше успешна. Промяната ви все още е запазена.',
  undoConflict: 'Това е променено след вашата редакция, затова не може да бъде отменено тук.',

  toggle: 'Инспектиране',
  hintTouch: 'Докоснете част, за да я изберете.',
  addAnother: 'Добавяне на още',
  element: 'Елемент',
  region: 'Област',
  pickerLabel: 'Изберете какво да маркирате',
  selectParent: 'Избор на родителския елемент',
  mention: 'Споменаване',
  remove: 'Премахване на {label}',
  selectedCount: '{n} избрани',
  limit: 'Можете да споменете до {limit} части наведнъж. Премахнете една, за да добавите друга.',
  stale: 'Променено. Изберете отново.',
  revoked: 'Вече не е налично',
  expired: 'Изтекло. Изберете отново.',
  details: 'Подробности',
  hideDetails: 'Скриване на подробностите',
  source: 'Източник',
  sourceHidden: 'Скрито за вашия достъп',
  sourceUnmapped: 'Не е свързано с източника',
  data: 'Данни',
  dataUnmapped: 'Източникът на данни не е свързан',
  picture: 'Изображение',
  pictureReplay: 'От ново изобразяване на тази версия',
  pending: 'Зареждане…',
  targetsLabel: 'Части, които можете да посочите',
  text: 'Текст',
  colour: 'Цвят',
  spacing: 'Разстояние',
  numberFormat: 'Формат на числата',
  mixed: 'Смесено',
  apply: 'Прилагане',
  cancel: 'Отказ',
  applying: 'Запазване…',
  saved: 'Запазено в черновата',
  undo: 'Отмяна',
  change: '{before} → {after}',
  refused: 'Това не може да се промени директно тук.',
  handoff: 'Променете го в чата',
  staleApply: 'Промени се, докато редактирахте. Изберете отново.',
  failed: 'Неуспешно запазване. Предишната версия е непроменена.',
  hintBar: 'Щракнете върху част, за да я споменете · Esc за изход',
  where: 'Къде',
  shows: 'Показва',
  changesHere: 'Променя се тук',
  showSource: 'Показване къде е дефинирано',
  hideSource: 'Скриване къде е дефинирано',
  cropUnavailable: 'Няма изрязване за този изглед',
  fromData: 'Идва от данни, затова не може да се презапише',
  noModel: 'Не се използва модел. Запазва нова чернова.',
  savedVersion: 'Запазено в чернова {version}',
  checkBeforePublish: 'Проверете отново преди публикуване',
  staleSubject: '{subject} се промени. Изберете отново.',
  staleGeneric: 'Промени се. Изберете отново.',
  shownAs: 'Показване като',
  shownNumber: 'Число',
  shownPercent: 'Процент',
  decimals: 'Десетични знаци',
  units: 'Мерна единица',
  unitKr: 'kr',
  unitThousandKr: 'хиляди kr',
  nowSuffix: '{value} сега',
  selectedToggle: '{n} избрани',
  grouping: 'Групиране',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Изберете отново, за да промените',
  colourInk: 'Мастило',
  colourMuted: 'Приглушено',
  colourBrand: 'Акцент',
  colourSuccess: 'Успех',
  colourWarning: 'Предупреждение',
  colourDanger: 'Опасност',
  saveUncertain: 'Това може да е запазено. Все още не можахме да го потвърдим.',
  checkAgain: 'Провери отново',
  margin: 'Външно поле',
  padding: 'Вътрешно поле',
  gap: 'Разстояние',
  rowGap: 'Разстояние между редовете',
  columnGap: 'Разстояние между колоните',
  sideTop: 'Горе',
  sideRight: 'Вдясно',
  sideBottom: 'Долу',
  sideLeft: 'Вляво',
  sameOnAllSides: 'Еднакво от всички страни',
  custom: 'Персонализирано…',
  customPx: 'Размер в px',
  smaller: 'По-малко',
  larger: 'По-голямо',
  pxValue: '{n} px',
  defaultValue: 'По подразбиране ({value})',
  channelText: 'Текст',
  channelBackground: 'Фон',
  channelBorder: 'Рамка',
  channelFill: 'Запълване',
  channelStroke: 'Линия',
  chooseColour: 'Изберете цвят за {channel}',
  reset: 'Нулиране',
  colourPaper: 'Хартия',
  colourSurface: 'Повърхност',
  colourSoft: 'Мек',
  colourLine: 'Линия',
  colourLineStrong: 'Плътна линия',
  colourSuccessBg: 'Фон за успех',
  colourWarningBg: 'Фон за предупреждение',
  colourDangerBg: 'Фон за опасност',
  colourSeries: 'Серия {n}',
  formatUnsupported: 'Този числов формат не може да се променя тук.',
  colourNone: 'Без цвят',
  formatScalarNative: 'оригинален формат',
  imageFit: 'Побиране',
  imageRatio: 'Съотношение',
  fitContain: 'Побиране изцяло',
  fitCover: 'Запълване',
  ratioIntrinsic: 'Оригинал',
  fitCoverUnsafe: 'Това изображение не може да се изрязва.',
  imageNotReady: 'Побирането може да се промени, щом изображението е готово.',
};

const cs: Record<InspectCopyKey, string> = {
  fieldContent: 'Obsah',
  inspectNodeParagraph: 'Odstavec',
  inspectNodeHeading: 'Nadpis',
  inspectNodeList: 'Seznam',
  viewOnly: 'Tohle můžete zobrazit, ale ne změnit.',
  undoFailed: 'Vrátit zpět se nepodařilo. Vaše změna zůstává uložená.',
  undoConflict: 'Po vaší úpravě se to změnilo, takže to tady nejde vrátit zpět.',

  toggle: 'Zkoumat',
  hintTouch: 'Klepnutím na část ji vyberete.',
  addAnother: 'Přidat další',
  element: 'Prvek',
  region: 'Oblast',
  pickerLabel: 'Vyberte, co označit',
  selectParent: 'Vybrat nadřazený prvek',
  mention: 'Zmínit',
  remove: 'Odebrat {label}',
  selectedCount: 'Vybráno: {n}',
  limit: 'Najednou můžete zmínit nejvýše {limit} částí. Odeberte jednu, chcete-li přidat další.',
  stale: 'Změněno. Vyberte znovu.',
  revoked: 'Již není k dispozici',
  expired: 'Platnost vypršela. Vyberte znovu.',
  details: 'Podrobnosti',
  hideDetails: 'Skrýt podrobnosti',
  source: 'Zdroj',
  sourceHidden: 'Skryto pro vaše oprávnění',
  sourceUnmapped: 'Nepropojeno se zdrojem',
  data: 'Data',
  dataUnmapped: 'Zdroj dat není propojen',
  picture: 'Obrázek',
  pictureReplay: 'Z nového vykreslení této verze',
  pending: 'Načítání…',
  targetsLabel: 'Části, na které můžete ukázat',
  text: 'Text',
  colour: 'Barva',
  spacing: 'Mezery',
  numberFormat: 'Formát čísla',
  mixed: 'Smíšené',
  apply: 'Použít',
  cancel: 'Zrušit',
  applying: 'Ukládání…',
  saved: 'Uloženo do konceptu',
  undo: 'Vrátit zpět',
  change: '{before} → {after}',
  refused: 'Tady to nelze změnit přímo.',
  handoff: 'Změnit v chatu',
  staleApply: 'Během úprav se to změnilo. Vyberte znovu.',
  failed: 'Uložení se nezdařilo. Předchozí verze zůstává beze změny.',
  hintBar: 'Klepnutím na část ji zmíníte · Esc pro ukončení',
  where: 'Kde',
  shows: 'Zobrazuje',
  changesHere: 'Mění se zde',
  showSource: 'Zobrazit, kde je definováno',
  hideSource: 'Skrýt, kde je definováno',
  cropUnavailable: 'Pro toto zobrazení není výřez',
  fromData: 'Pochází z dat, nelze to přepsat',
  noModel: 'Nepoužívá se žádný model. Uloží nový koncept.',
  savedVersion: 'Uloženo do konceptu {version}',
  checkBeforePublish: 'Před zveřejněním znovu zkontrolujte',
  staleSubject: '{subject} se změnil(a). Vyberte znovu.',
  staleGeneric: 'Změnilo se to. Vyberte znovu.',
  shownAs: 'Zobrazit jako',
  shownNumber: 'Číslo',
  shownPercent: 'Procento',
  decimals: 'Desetinná místa',
  units: 'Jednotka',
  unitKr: 'kr',
  unitThousandKr: 'tisíce kr',
  nowSuffix: '{value} nyní',
  selectedToggle: 'Vybráno: {n}',
  grouping: 'Oddělování tisíců',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Pro změnu vyberte znovu',
  colourInk: 'Inkoust',
  colourMuted: 'Tlumená',
  colourBrand: 'Zvýraznění',
  colourSuccess: 'Úspěch',
  colourWarning: 'Upozornění',
  colourDanger: 'Nebezpečí',
  saveUncertain: 'Možná se to uložilo. Zatím jsme to nemohli potvrdit.',
  checkAgain: 'Zkontrolovat znovu',
  margin: 'Vnější okraj',
  padding: 'Vnitřní okraj',
  gap: 'Mezera',
  rowGap: 'Mezera mezi řádky',
  columnGap: 'Mezera mezi sloupci',
  sideTop: 'Nahoře',
  sideRight: 'Vpravo',
  sideBottom: 'Dole',
  sideLeft: 'Vlevo',
  sameOnAllSides: 'Stejně na všech stranách',
  custom: 'Vlastní…',
  customPx: 'Velikost v px',
  smaller: 'Menší',
  larger: 'Větší',
  pxValue: '{n} px',
  defaultValue: 'Výchozí ({value})',
  channelText: 'Text',
  channelBackground: 'Pozadí',
  channelBorder: 'Ohraničení',
  channelFill: 'Výplň',
  channelStroke: 'Tah',
  chooseColour: 'Vyberte barvu pro {channel}',
  reset: 'Obnovit',
  colourPaper: 'Papír',
  colourSurface: 'Plocha',
  colourSoft: 'Jemná',
  colourLine: 'Čára',
  colourLineStrong: 'Výrazná čára',
  colourSuccessBg: 'Pozadí úspěchu',
  colourWarningBg: 'Pozadí upozornění',
  colourDangerBg: 'Pozadí nebezpečí',
  colourSeries: 'Řada {n}',
  formatUnsupported: 'Tento formát čísel zde nelze změnit.',
  colourNone: 'Bez barvy',
  formatScalarNative: 'původní formát',
  imageFit: 'Přizpůsobení',
  imageRatio: 'Poměr',
  fitContain: 'Celý obrázek',
  fitCover: 'Vyplnit',
  ratioIntrinsic: 'Původní',
  fitCoverUnsafe: 'Tento obrázek nelze oříznout.',
  imageNotReady: 'Přizpůsobení lze změnit, až bude obrázek připravený.',
};

const da: Record<InspectCopyKey, string> = {
  fieldContent: 'Indhold',
  inspectNodeParagraph: 'Afsnit',
  inspectNodeHeading: 'Overskrift',
  inspectNodeList: 'Liste',
  viewOnly: 'Du kan se dette, men ikke ændre det.',
  undoFailed: 'Det kunne ikke fortrydes. Din ændring er stadig gemt.',
  undoConflict: 'Det er ændret efter din redigering, så den kan ikke fortrydes her.',

  toggle: 'Inspicér',
  hintTouch: 'Tryk på en del for at vælge den.',
  addAnother: 'Tilføj en mere',
  element: 'Element',
  region: 'Område',
  pickerLabel: 'Vælg, hvad der skal markeres',
  selectParent: 'Vælg overordnet',
  mention: 'Nævn',
  remove: 'Fjern {label}',
  selectedCount: '{n} valgt',
  limit: 'Du kan nævne op til {limit} dele ad gangen. Fjern en for at tilføje en anden.',
  stale: 'Ændret. Vælg den igen.',
  revoked: 'Ikke længere tilgængelig',
  expired: 'Udløbet. Vælg den igen.',
  details: 'Detaljer',
  hideDetails: 'Skjul detaljer',
  source: 'Kilde',
  sourceHidden: 'Skjult for din adgang',
  sourceUnmapped: 'Ikke koblet til kilden',
  data: 'Data',
  dataUnmapped: 'Datakilden er ikke koblet',
  picture: 'Billede',
  pictureReplay: 'Fra en ny gengivelse af denne version',
  pending: 'Indlæser…',
  targetsLabel: 'Dele, du kan pege på',
  text: 'Tekst',
  colour: 'Farve',
  spacing: 'Afstand',
  numberFormat: 'Talformat',
  mixed: 'Blandet',
  apply: 'Anvend',
  cancel: 'Annullér',
  applying: 'Gemmer…',
  saved: 'Gemt i kladden',
  undo: 'Fortryd',
  change: '{before} → {after}',
  refused: 'Det kan ikke ændres direkte her.',
  handoff: 'Ændr det i chatten',
  staleApply: 'Den ændrede sig, mens du redigerede. Vælg den igen.',
  failed: 'Kunne ikke gemme. Den tidligere version er uændret.',
  hintBar: 'Klik på en del for at nævne den · Esc for at forlade',
  where: 'Hvor',
  shows: 'Viser',
  changesHere: 'Ændres her',
  showSource: 'Vis, hvor den er defineret',
  hideSource: 'Skjul, hvor den er defineret',
  cropUnavailable: 'Intet udsnit for denne visning',
  fromData: 'Kommer fra data, så den kan ikke overskrives',
  noModel: 'Der bruges ingen model. Gemmer en ny kladde.',
  savedVersion: 'Gemt i kladde {version}',
  checkBeforePublish: 'Tjek igen før udgivelse',
  staleSubject: '{subject} er ændret. Vælg igen.',
  staleGeneric: 'Den er ændret. Vælg igen.',
  shownAs: 'Vises som',
  shownNumber: 'Tal',
  shownPercent: 'Procent',
  decimals: 'Decimaler',
  units: 'Enhed',
  unitKr: 'kr',
  unitThousandKr: 'tusind kr',
  nowSuffix: '{value} nu',
  selectedToggle: '{n} valgt',
  grouping: 'Gruppering',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Vælg den igen for at ændre den',
  colourInk: 'Blæk',
  colourMuted: 'Dæmpet',
  colourBrand: 'Accent',
  colourSuccess: 'Succes',
  colourWarning: 'Advarsel',
  colourDanger: 'Fare',
  saveUncertain: 'Det er muligvis gemt. Vi kunne ikke bekræfte det endnu.',
  checkAgain: 'Tjek igen',
  margin: 'Margen',
  padding: 'Indvendig margen',
  gap: 'Afstand',
  rowGap: 'Rækkeafstand',
  columnGap: 'Kolonneafstand',
  sideTop: 'Øverst',
  sideRight: 'Højre',
  sideBottom: 'Nederst',
  sideLeft: 'Venstre',
  sameOnAllSides: 'Ens på alle sider',
  custom: 'Tilpasset…',
  customPx: 'Størrelse i px',
  smaller: 'Mindre',
  larger: 'Større',
  pxValue: '{n} px',
  defaultValue: 'Standard ({value})',
  channelText: 'Tekst',
  channelBackground: 'Baggrund',
  channelBorder: 'Kant',
  channelFill: 'Fyld',
  channelStroke: 'Streg',
  chooseColour: 'Vælg farve til {channel}',
  reset: 'Nulstil',
  colourPaper: 'Papir',
  colourSurface: 'Overflade',
  colourSoft: 'Blød',
  colourLine: 'Linje',
  colourLineStrong: 'Tydelig linje',
  colourSuccessBg: 'Baggrund for succes',
  colourWarningBg: 'Baggrund for advarsel',
  colourDangerBg: 'Baggrund for fare',
  colourSeries: 'Serie {n}',
  formatUnsupported: 'Dette talformat kan ikke ændres her.',
  colourNone: 'Ingen farve',
  formatScalarNative: 'oprindeligt format',
  imageFit: 'Tilpasning',
  imageRatio: 'Forhold',
  fitContain: 'Hele billedet',
  fitCover: 'Udfyld',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Dette billede kan ikke beskæres.',
  imageNotReady: 'Tilpasningen kan ændres, når billedet er klar.',
};

const de: Record<InspectCopyKey, string> = {
  fieldContent: 'Inhalt',
  inspectNodeParagraph: 'Absatz',
  inspectNodeHeading: 'Überschrift',
  inspectNodeList: 'Liste',
  viewOnly: 'Sie können das ansehen, aber nicht ändern.',
  undoFailed: 'Rückgängig machen fehlgeschlagen. Ihre Änderung ist weiterhin gespeichert.',
  undoConflict:
    'Das wurde nach Ihrer Bearbeitung geändert und kann hier nicht rückgängig gemacht werden.',

  toggle: 'Untersuchen',
  hintTouch: 'Tippe auf einen Teil, um ihn auszuwählen.',
  addAnother: 'Weiteren hinzufügen',
  element: 'Element',
  region: 'Bereich',
  pickerLabel: 'Auswahl festlegen',
  selectParent: 'Übergeordnetes auswählen',
  mention: 'Erwähnen',
  remove: '{label} entfernen',
  selectedCount: '{n} ausgewählt',
  limit:
    'Sie können bis zu {limit} Teile gleichzeitig angeben. Entfernen Sie einen, um einen weiteren hinzuzufügen.',
  stale: 'Geändert. Bitte erneut auswählen.',
  revoked: 'Nicht mehr verfügbar',
  expired: 'Abgelaufen. Bitte erneut auswählen.',
  details: 'Details',
  hideDetails: 'Details ausblenden',
  source: 'Quelle',
  sourceHidden: 'Aufgrund Ihrer Zugriffsrechte ausgeblendet',
  sourceUnmapped: 'Keiner Quelle zugeordnet',
  data: 'Daten',
  dataUnmapped: 'Datenquelle nicht zugeordnet',
  picture: 'Bild',
  pictureReplay: 'Aus einer neuen Darstellung dieser Version',
  pending: 'Wird geladen…',
  targetsLabel: 'Teile, auf die Sie zeigen können',
  text: 'Text',
  colour: 'Farbe',
  spacing: 'Abstand',
  numberFormat: 'Zahlenformat',
  mixed: 'Gemischt',
  apply: 'Übernehmen',
  cancel: 'Abbrechen',
  applying: 'Wird gespeichert…',
  saved: 'Im Entwurf gespeichert',
  undo: 'Rückgängig',
  change: '{before} → {after}',
  refused: 'Das lässt sich hier nicht direkt ändern.',
  handoff: 'Im Chat ändern',
  staleApply: 'Das Element wurde während Ihrer Bearbeitung geändert. Wählen Sie es erneut aus.',
  failed: 'Speichern fehlgeschlagen. Die vorherige Version ist unverändert.',
  hintBar: 'Klicken Sie auf einen Teil, um ihn zu erwähnen · Esc zum Beenden',
  where: 'Wo',
  shows: 'Zeigt',
  changesHere: 'Änderungen hier',
  showSource: 'Zeigen, wo es definiert ist',
  hideSource: 'Ausblenden, wo es definiert ist',
  cropUnavailable: 'Kein Ausschnitt für diese Ansicht',
  fromData: 'Stammt aus Daten und kann nicht überschrieben werden',
  noModel: 'Es wird kein Modell verwendet. Speichert einen neuen Entwurf.',
  savedVersion: 'Im Entwurf {version} gespeichert',
  checkBeforePublish: 'Vor dem Veröffentlichen erneut prüfen',
  staleSubject: '{subject} wurde geändert. Bitte erneut auswählen.',
  staleGeneric: 'Es wurde geändert. Bitte erneut auswählen.',
  shownAs: 'Anzeigen als',
  shownNumber: 'Zahl',
  shownPercent: 'Prozent',
  decimals: 'Dezimalstellen',
  units: 'Einheit',
  unitKr: 'kr',
  unitThousandKr: 'Tausend kr',
  nowSuffix: '{value} aktuell',
  selectedToggle: '{n} ausgewählt',
  grouping: 'Tausendertrennung',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Zum Ändern erneut auswählen',
  colourInk: 'Tinte',
  colourMuted: 'Gedämpft',
  colourBrand: 'Akzent',
  colourSuccess: 'Erfolg',
  colourWarning: 'Warnung',
  colourDanger: 'Gefahr',
  saveUncertain: 'Das wurde möglicherweise gespeichert. Wir konnten es noch nicht bestätigen.',
  checkAgain: 'Erneut prüfen',
  margin: 'Außenabstand',
  padding: 'Innenabstand',
  gap: 'Abstand',
  rowGap: 'Zeilenabstand',
  columnGap: 'Spaltenabstand',
  sideTop: 'Oben',
  sideRight: 'Rechts',
  sideBottom: 'Unten',
  sideLeft: 'Links',
  sameOnAllSides: 'Auf allen Seiten gleich',
  custom: 'Benutzerdefiniert…',
  customPx: 'Größe in px',
  smaller: 'Kleiner',
  larger: 'Größer',
  pxValue: '{n} px',
  defaultValue: 'Standard ({value})',
  channelText: 'Text',
  channelBackground: 'Hintergrund',
  channelBorder: 'Rahmen',
  channelFill: 'Füllung',
  channelStroke: 'Kontur',
  chooseColour: 'Farbe für {channel} wählen',
  reset: 'Zurücksetzen',
  colourPaper: 'Papier',
  colourSurface: 'Fläche',
  colourSoft: 'Sanft',
  colourLine: 'Linie',
  colourLineStrong: 'Kräftige Linie',
  colourSuccessBg: 'Hintergrund Erfolg',
  colourWarningBg: 'Hintergrund Warnung',
  colourDangerBg: 'Hintergrund Gefahr',
  colourSeries: 'Reihe {n}',
  formatUnsupported: 'Dieses Zahlenformat kann hier nicht geändert werden.',
  colourNone: 'Keine Farbe',
  formatScalarNative: 'ursprüngliches Format',
  imageFit: 'Einpassung',
  imageRatio: 'Seitenverhältnis',
  fitContain: 'Ganz zeigen',
  fitCover: 'Füllen',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Dieses Bild kann nicht zugeschnitten werden.',
  imageNotReady: 'Die Einpassung lässt sich ändern, sobald das Bild bereit ist.',
};

const el: Record<InspectCopyKey, string> = {
  fieldContent: 'Περιεχόμενο',
  inspectNodeParagraph: 'Παράγραφος',
  inspectNodeHeading: 'Επικεφαλίδα',
  inspectNodeList: 'Λίστα',
  viewOnly: 'Μπορείτε να το δείτε, αλλά όχι να το αλλάξετε.',
  undoFailed: 'Η αναίρεση απέτυχε. Η αλλαγή σας παραμένει αποθηκευμένη.',
  undoConflict: 'Άλλαξε μετά την επεξεργασία σας, οπότε δεν μπορεί να αναιρεθεί εδώ.',

  toggle: 'Επιθεώρηση',
  hintTouch: 'Πατήστε ένα τμήμα για να το επιλέξετε.',
  addAnother: 'Προσθήκη ακόμη ενός',
  element: 'Στοιχείο',
  region: 'Περιοχή',
  pickerLabel: 'Επιλέξτε τι θα επισημάνετε',
  selectParent: 'Επιλογή γονικού',
  mention: 'Αναφορά',
  remove: 'Αφαίρεση {label}',
  selectedCount: 'Επιλέχθηκαν {n}',
  limit: 'Μπορείτε να αναφέρετε έως {limit} τμήματα τη φορά. Αφαιρέστε ένα για να προσθέσετε άλλο.',
  stale: 'Άλλαξε. Επιλέξτε το ξανά.',
  revoked: 'Δεν είναι πλέον διαθέσιμο',
  expired: 'Έληξε. Επιλέξτε το ξανά.',
  details: 'Λεπτομέρειες',
  hideDetails: 'Απόκρυψη λεπτομερειών',
  source: 'Προέλευση',
  sourceHidden: 'Κρυφό για την πρόσβασή σας',
  sourceUnmapped: 'Δεν συνδέεται με την προέλευση',
  data: 'Δεδομένα',
  dataUnmapped: 'Η πηγή δεδομένων δεν είναι συνδεδεμένη',
  picture: 'Εικόνα',
  pictureReplay: 'Από νέα απόδοση αυτής της έκδοσης',
  pending: 'Φόρτωση…',
  targetsLabel: 'Τμήματα στα οποία μπορείτε να δείξετε',
  text: 'Κείμενο',
  colour: 'Χρώμα',
  spacing: 'Αποστάσεις',
  numberFormat: 'Μορφή αριθμών',
  mixed: 'Μικτό',
  apply: 'Εφαρμογή',
  cancel: 'Ακύρωση',
  applying: 'Αποθήκευση…',
  saved: 'Αποθηκεύτηκε στο πρόχειρο',
  undo: 'Αναίρεση',
  change: '{before} → {after}',
  refused: 'Αυτό δεν μπορεί να αλλάξει απευθείας εδώ.',
  handoff: 'Αλλάξτε το στη συνομιλία',
  staleApply: 'Άλλαξε ενώ το επεξεργαζόσασταν. Επιλέξτε το ξανά.',
  failed: 'Η αποθήκευση απέτυχε. Η προηγούμενη έκδοση παραμένει αμετάβλητη.',
  hintBar: 'Κάντε κλικ σε ένα τμήμα για να το αναφέρετε · Esc για έξοδο',
  where: 'Πού',
  shows: 'Εμφανίζει',
  changesHere: 'Αλλάζει εδώ',
  showSource: 'Εμφάνιση του σημείου ορισμού',
  hideSource: 'Απόκρυψη του σημείου ορισμού',
  cropUnavailable: 'Δεν υπάρχει απόκομμα για αυτή την προβολή',
  fromData: 'Προέρχεται από δεδομένα, άρα δεν αντικαθίσταται',
  noModel: 'Δεν χρησιμοποιείται μοντέλο. Αποθηκεύει νέο πρόχειρο.',
  savedVersion: 'Αποθηκεύτηκε στο πρόχειρο {version}',
  checkBeforePublish: 'Ελέγξτε ξανά πριν από τη δημοσίευση',
  staleSubject: '{subject}: άλλαξε. Επιλέξτε ξανά.',
  staleGeneric: 'Άλλαξε. Επιλέξτε ξανά.',
  shownAs: 'Εμφάνιση ως',
  shownNumber: 'Αριθμός',
  shownPercent: 'Ποσοστό',
  decimals: 'Δεκαδικά',
  units: 'Μονάδα',
  unitKr: 'kr',
  unitThousandKr: 'χιλιάδες kr',
  nowSuffix: '{value} τώρα',
  selectedToggle: 'Επιλέχθηκαν {n}',
  grouping: 'Διαχωριστικό χιλιάδων',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Επιλέξτε το ξανά για να το αλλάξετε',
  colourInk: 'Μελάνι',
  colourMuted: 'Απαλό',
  colourBrand: 'Έμφαση',
  colourSuccess: 'Επιτυχία',
  colourWarning: 'Προειδοποίηση',
  colourDanger: 'Κίνδυνος',
  saveUncertain: 'Αυτό μπορεί να έχει αποθηκευτεί. Δεν μπορέσαμε να το επιβεβαιώσουμε ακόμη.',
  checkAgain: 'Έλεγχος ξανά',
  margin: 'Περιθώριο',
  padding: 'Εσωτερικό περιθώριο',
  gap: 'Κενό',
  rowGap: 'Κενό γραμμών',
  columnGap: 'Κενό στηλών',
  sideTop: 'Πάνω',
  sideRight: 'Δεξιά',
  sideBottom: 'Κάτω',
  sideLeft: 'Αριστερά',
  sameOnAllSides: 'Ίδιο σε όλες τις πλευρές',
  custom: 'Προσαρμογή…',
  customPx: 'Μέγεθος σε px',
  smaller: 'Μικρότερο',
  larger: 'Μεγαλύτερο',
  pxValue: '{n} px',
  defaultValue: 'Προεπιλογή ({value})',
  channelText: 'Κείμενο',
  channelBackground: 'Φόντο',
  channelBorder: 'Περίγραμμα',
  channelFill: 'Γέμισμα',
  channelStroke: 'Γραμμή',
  chooseColour: 'Επιλέξτε χρώμα για {channel}',
  reset: 'Επαναφορά',
  colourPaper: 'Χαρτί',
  colourSurface: 'Επιφάνεια',
  colourSoft: 'Απαλό',
  colourLine: 'Γραμμή',
  colourLineStrong: 'Έντονη γραμμή',
  colourSuccessBg: 'Φόντο επιτυχίας',
  colourWarningBg: 'Φόντο προειδοποίησης',
  colourDangerBg: 'Φόντο κινδύνου',
  colourSeries: 'Σειρά {n}',
  formatUnsupported: 'Αυτή η μορφή αριθμών δεν μπορεί να αλλάξει εδώ.',
  colourNone: 'Χωρίς χρώμα',
  formatScalarNative: 'αρχική μορφή',
  imageFit: 'Προσαρμογή',
  imageRatio: 'Αναλογία',
  fitContain: 'Ολόκληρη',
  fitCover: 'Γέμισμα',
  ratioIntrinsic: 'Αρχική',
  fitCoverUnsafe: 'Αυτή η εικόνα δεν μπορεί να περικοπεί.',
  imageNotReady: 'Η προσαρμογή μπορεί να αλλάξει μόλις η εικόνα είναι έτοιμη.',
};

const es: Record<InspectCopyKey, string> = {
  fieldContent: 'Contenido',
  inspectNodeParagraph: 'Párrafo',
  inspectNodeHeading: 'Encabezado',
  inspectNodeList: 'Lista',
  viewOnly: 'Puedes ver esto, pero no cambiarlo.',
  undoFailed: 'No se ha podido deshacer. Tu cambio sigue guardado.',
  undoConflict: 'Ha cambiado después de tu edición, así que no se puede deshacer aquí.',

  toggle: 'Inspeccionar',
  hintTouch: 'Toca una parte para seleccionarla.',
  addAnother: 'Añadir otra',
  element: 'Elemento',
  region: 'Zona',
  pickerLabel: 'Elige qué seleccionar',
  selectParent: 'Seleccionar el contenedor',
  mention: 'Mencionar',
  remove: 'Quitar {label}',
  selectedCount: '{n} seleccionadas',
  limit: 'Puedes mencionar hasta {limit} partes a la vez. Quita una para añadir otra.',
  stale: 'Ha cambiado. Vuelve a seleccionarla.',
  revoked: 'Ya no está disponible',
  expired: 'Ha caducado. Vuelve a seleccionarla.',
  details: 'Detalles',
  hideDetails: 'Ocultar detalles',
  source: 'Origen',
  sourceHidden: 'Oculto para tu nivel de acceso',
  sourceUnmapped: 'Sin vincular al origen',
  data: 'Datos',
  dataUnmapped: 'Fuente de datos sin vincular',
  picture: 'Imagen',
  pictureReplay: 'De una nueva representación de esta versión',
  pending: 'Cargando…',
  targetsLabel: 'Partes que puedes señalar',
  text: 'Texto',
  colour: 'Color',
  spacing: 'Espaciado',
  numberFormat: 'Formato de número',
  mixed: 'Mixto',
  apply: 'Aplicar',
  cancel: 'Cancelar',
  applying: 'Guardando…',
  saved: 'Guardado en el borrador',
  undo: 'Deshacer',
  change: '{before} → {after}',
  refused: 'Esto no se puede cambiar directamente aquí.',
  handoff: 'Cámbialo en el chat',
  staleApply: 'Ha cambiado mientras lo editabas. Vuelve a seleccionarlo.',
  failed: 'No se pudo guardar. La versión anterior no ha cambiado.',
  hintBar: 'Haz clic en una parte para mencionarla · Esc para salir',
  where: 'Dónde',
  shows: 'Muestra',
  changesHere: 'Cambia aquí',
  showSource: 'Mostrar dónde se define',
  hideSource: 'Ocultar dónde se define',
  cropUnavailable: 'No hay recorte para esta vista',
  fromData: 'Viene de los datos, así que no se puede sobrescribir',
  noModel: 'No se usa ningún modelo. Guarda un nuevo borrador.',
  savedVersion: 'Guardado en el borrador {version}',
  checkBeforePublish: 'Comprueba de nuevo antes de publicar',
  staleSubject: '{subject} ha cambiado. Vuelve a seleccionar.',
  staleGeneric: 'Ha cambiado. Vuelve a seleccionar.',
  shownAs: 'Mostrar como',
  shownNumber: 'Número',
  shownPercent: 'Porcentaje',
  decimals: 'Decimales',
  units: 'Unidad',
  unitKr: 'kr',
  unitThousandKr: 'miles de kr',
  nowSuffix: '{value} actual',
  selectedToggle: '{n} seleccionadas',
  grouping: 'Separador de miles',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Vuelve a seleccionarla para cambiarla',
  colourInk: 'Tinta',
  colourMuted: 'Atenuado',
  colourBrand: 'Acento',
  colourSuccess: 'Éxito',
  colourWarning: 'Advertencia',
  colourDanger: 'Peligro',
  saveUncertain: 'Puede que se haya guardado. Aún no hemos podido confirmarlo.',
  checkAgain: 'Comprobar de nuevo',
  margin: 'Margen',
  padding: 'Relleno',
  gap: 'Espacio',
  rowGap: 'Espacio entre filas',
  columnGap: 'Espacio entre columnas',
  sideTop: 'Arriba',
  sideRight: 'Derecha',
  sideBottom: 'Abajo',
  sideLeft: 'Izquierda',
  sameOnAllSides: 'Igual en todos los lados',
  custom: 'Personalizado…',
  customPx: 'Tamaño en px',
  smaller: 'Más pequeño',
  larger: 'Más grande',
  pxValue: '{n} px',
  defaultValue: 'Predeterminado ({value})',
  channelText: 'Texto',
  channelBackground: 'Fondo',
  channelBorder: 'Borde',
  channelFill: 'Relleno',
  channelStroke: 'Trazo',
  chooseColour: 'Elige el color de {channel}',
  reset: 'Restablecer',
  colourPaper: 'Papel',
  colourSurface: 'Superficie',
  colourSoft: 'Suave',
  colourLine: 'Línea',
  colourLineStrong: 'Línea marcada',
  colourSuccessBg: 'Fondo de éxito',
  colourWarningBg: 'Fondo de advertencia',
  colourDangerBg: 'Fondo de peligro',
  colourSeries: 'Serie {n}',
  formatUnsupported: 'Este formato de número no se puede cambiar aquí.',
  colourNone: 'Sin color',
  formatScalarNative: 'formato original',
  imageFit: 'Ajuste',
  imageRatio: 'Proporción',
  fitContain: 'Contener',
  fitCover: 'Cubrir',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Esta imagen no se puede recortar.',
  imageNotReady: 'El ajuste se puede cambiar cuando la imagen esté lista.',
};

const et: Record<InspectCopyKey, string> = {
  fieldContent: 'Sisu',
  inspectNodeParagraph: 'Lõik',
  inspectNodeHeading: 'Pealkiri',
  inspectNodeList: 'Loend',
  viewOnly: 'Saad seda vaadata, kuid mitte muuta.',
  undoFailed: 'Tagasivõtmine ebaõnnestus. Sinu muudatus on endiselt salvestatud.',
  undoConflict: 'See muutus pärast sinu muudatust, nii et seda ei saa siin tagasi võtta.',

  toggle: 'Uuri',
  hintTouch: 'Valimiseks puudutage osa.',
  addAnother: 'Lisa veel üks',
  element: 'Element',
  region: 'Ala',
  pickerLabel: 'Valige, mida märkida',
  selectParent: 'Vali ülemelement',
  mention: 'Maini',
  remove: 'Eemalda {label}',
  selectedCount: 'Valitud: {n}',
  limit: 'Korraga saate mainida kuni {limit} osa. Teise lisamiseks eemaldage üks.',
  stale: 'Muutunud. Valige uuesti.',
  revoked: 'Pole enam saadaval',
  expired: 'Aegunud. Valige uuesti.',
  details: 'Üksikasjad',
  hideDetails: 'Peida üksikasjad',
  source: 'Allikas',
  sourceHidden: 'Teie juurdepääsu jaoks peidetud',
  sourceUnmapped: 'Pole allikaga seotud',
  data: 'Andmed',
  dataUnmapped: 'Andmeallikas pole seotud',
  picture: 'Pilt',
  pictureReplay: 'Selle versiooni uuest renderdusest',
  pending: 'Laadimine…',
  targetsLabel: 'Osad, millele saate osutada',
  text: 'Tekst',
  colour: 'Värv',
  spacing: 'Vahe',
  numberFormat: 'Arvuvorming',
  mixed: 'Segatud',
  apply: 'Rakenda',
  cancel: 'Tühista',
  applying: 'Salvestamine…',
  saved: 'Salvestatud mustandisse',
  undo: 'Võta tagasi',
  change: '{before} → {after}',
  refused: 'Seda ei saa siin otse muuta.',
  handoff: 'Muutke seda vestluses',
  staleApply: 'See muutus muutmise ajal. Valige uuesti.',
  failed: 'Salvestamine ebaõnnestus. Eelmine versioon on muutmata.',
  hintBar: 'Mainimiseks klõpsake osal · Esc väljumiseks',
  where: 'Kus',
  shows: 'Näitab',
  changesHere: 'Muutub siin',
  showSource: 'Näita, kus see on määratud',
  hideSource: 'Peida, kus see on määratud',
  cropUnavailable: 'Selle vaate jaoks pole väljalõiget',
  fromData: 'Pärineb andmetest, seega ei saa seda üle kirjutada',
  noModel: 'Mudelit ei kasutata. Salvestab uue mustandi.',
  savedVersion: 'Salvestatud mustandisse {version}',
  checkBeforePublish: 'Kontrollige enne avaldamist uuesti',
  staleSubject: '{subject}: muutunud. Valige uuesti.',
  staleGeneric: 'See muutus. Valige uuesti.',
  shownAs: 'Näita kujul',
  shownNumber: 'Arv',
  shownPercent: 'Protsent',
  decimals: 'Kümnendkohad',
  units: 'Ühik',
  unitKr: 'kr',
  unitThousandKr: 'tuhat kr',
  nowSuffix: '{value} praegu',
  selectedToggle: 'Valitud: {n}',
  grouping: 'Tuhandete eraldaja',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Muutmiseks valige uuesti',
  colourInk: 'Tint',
  colourMuted: 'Summutatud',
  colourBrand: 'Rõhk',
  colourSuccess: 'Õnnestumine',
  colourWarning: 'Hoiatus',
  colourDanger: 'Oht',
  saveUncertain: 'See võis salvestuda. Me ei saanud seda veel kinnitada.',
  checkAgain: 'Kontrolli uuesti',
  margin: 'Välisveeris',
  padding: 'Siseveeris',
  gap: 'Vahe',
  rowGap: 'Ridade vahe',
  columnGap: 'Veergude vahe',
  sideTop: 'Üleval',
  sideRight: 'Paremal',
  sideBottom: 'All',
  sideLeft: 'Vasakul',
  sameOnAllSides: 'Kõigil külgedel sama',
  custom: 'Kohandatud…',
  customPx: 'Suurus pikslites',
  smaller: 'Väiksem',
  larger: 'Suurem',
  pxValue: '{n} px',
  defaultValue: 'Vaikimisi ({value})',
  channelText: 'Tekst',
  channelBackground: 'Taust',
  channelBorder: 'Ääris',
  channelFill: 'Täide',
  channelStroke: 'Joon',
  chooseColour: 'Vali värv: {channel}',
  reset: 'Lähtesta',
  colourPaper: 'Paber',
  colourSurface: 'Pind',
  colourSoft: 'Pehme',
  colourLine: 'Joon',
  colourLineStrong: 'Tugev joon',
  colourSuccessBg: 'Õnnestumise taust',
  colourWarningBg: 'Hoiatuse taust',
  colourDangerBg: 'Ohu taust',
  colourSeries: 'Seeria {n}',
  formatUnsupported: 'Seda numbrivormingut ei saa siin muuta.',
  colourNone: 'Värv puudub',
  formatScalarNative: 'algne vorming',
  imageFit: 'Sobitus',
  imageRatio: 'Suhe',
  fitContain: 'Mahuta',
  fitCover: 'Täida',
  ratioIntrinsic: 'Algne',
  fitCoverUnsafe: 'Seda pilti ei saa kärpida.',
  imageNotReady: 'Sobitust saab muuta, kui pilt on valmis.',
};

const fi: Record<InspectCopyKey, string> = {
  fieldContent: 'Sisältö',
  inspectNodeParagraph: 'Kappale',
  inspectNodeHeading: 'Otsikko',
  inspectNodeList: 'Luettelo',
  viewOnly: 'Voit katsoa tätä, mutta et muuttaa sitä.',
  undoFailed: 'Kumoaminen epäonnistui. Muutoksesi on yhä tallennettu.',
  undoConflict: 'Tämä muuttui muokkauksesi jälkeen, joten sitä ei voi kumota tässä.',

  toggle: 'Tarkastele',
  hintTouch: 'Valitse osa napauttamalla.',
  addAnother: 'Lisää toinen',
  element: 'Elementti',
  region: 'Alue',
  pickerLabel: 'Valitse, mitä merkitään',
  selectParent: 'Valitse yläelementti',
  mention: 'Mainitse',
  remove: 'Poista {label}',
  selectedCount: '{n} valittu',
  limit: 'Voit mainita kerralla enintään {limit} osaa. Poista yksi lisätäksesi toisen.',
  stale: 'Muuttunut. Valitse uudelleen.',
  revoked: 'Ei enää käytettävissä',
  expired: 'Vanhentunut. Valitse uudelleen.',
  details: 'Tiedot',
  hideDetails: 'Piilota tiedot',
  source: 'Lähde',
  sourceHidden: 'Piilotettu käyttöoikeuksiesi vuoksi',
  sourceUnmapped: 'Ei yhdistetty lähteeseen',
  data: 'Tiedot',
  dataUnmapped: 'Tietolähdettä ei ole yhdistetty',
  picture: 'Kuva',
  pictureReplay: 'Tämän version uudesta piirrosta',
  pending: 'Ladataan…',
  targetsLabel: 'Osat, joihin voit osoittaa',
  text: 'Teksti',
  colour: 'Väri',
  spacing: 'Välistys',
  numberFormat: 'Lukumuoto',
  mixed: 'Vaihteleva',
  apply: 'Käytä',
  cancel: 'Peruuta',
  applying: 'Tallennetaan…',
  saved: 'Tallennettu luonnokseen',
  undo: 'Kumoa',
  change: '{before} → {after}',
  refused: 'Tätä ei voi muuttaa suoraan tässä.',
  handoff: 'Muuta tätä keskustelussa',
  staleApply: 'Tämä muuttui muokkauksen aikana. Valitse uudelleen.',
  failed: 'Tallennus epäonnistui. Edellinen versio on ennallaan.',
  hintBar: 'Mainitse osa napsauttamalla · Esc lopettaa',
  where: 'Missä',
  shows: 'Näyttää',
  changesHere: 'Muuttuu tässä',
  showSource: 'Näytä, missä se on määritelty',
  hideSource: 'Piilota, missä se on määritelty',
  cropUnavailable: 'Tälle näkymälle ei ole rajausta',
  fromData: 'Tulee tiedoista, joten sitä ei voi korvata',
  noModel: 'Mallia ei käytetä. Tallentaa uuden luonnoksen.',
  savedVersion: 'Tallennettu luonnokseen {version}',
  checkBeforePublish: 'Tarkista uudelleen ennen julkaisua',
  staleSubject: '{subject} muuttui. Valitse uudelleen.',
  staleGeneric: 'Se muuttui. Valitse uudelleen.',
  shownAs: 'Näytä muodossa',
  shownNumber: 'Luku',
  shownPercent: 'Prosentti',
  decimals: 'Desimaalit',
  units: 'Yksikkö',
  unitKr: 'kr',
  unitThousandKr: 'tuhatta kr',
  nowSuffix: '{value} nyt',
  selectedToggle: '{n} valittu',
  grouping: 'Tuhaterotin',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Valitse uudelleen muuttaaksesi',
  colourInk: 'Muste',
  colourMuted: 'Vaimennettu',
  colourBrand: 'Korostus',
  colourSuccess: 'Onnistuminen',
  colourWarning: 'Varoitus',
  colourDanger: 'Vaara',
  saveUncertain: 'Tämä saattoi tallentua. Emme voineet vielä vahvistaa sitä.',
  checkAgain: 'Tarkista uudelleen',
  margin: 'Marginaali',
  padding: 'Sisennys',
  gap: 'Väli',
  rowGap: 'Rivien väli',
  columnGap: 'Sarakkeiden väli',
  sideTop: 'Ylhäällä',
  sideRight: 'Oikealla',
  sideBottom: 'Alhaalla',
  sideLeft: 'Vasemmalla',
  sameOnAllSides: 'Sama kaikilla sivuilla',
  custom: 'Mukautettu…',
  customPx: 'Koko pikseleinä',
  smaller: 'Pienempi',
  larger: 'Suurempi',
  pxValue: '{n} px',
  defaultValue: 'Oletus ({value})',
  channelText: 'Teksti',
  channelBackground: 'Tausta',
  channelBorder: 'Reunus',
  channelFill: 'Täyttö',
  channelStroke: 'Viiva',
  chooseColour: 'Valitse väri: {channel}',
  reset: 'Palauta',
  colourPaper: 'Paperi',
  colourSurface: 'Pinta',
  colourSoft: 'Pehmeä',
  colourLine: 'Viiva',
  colourLineStrong: 'Vahva viiva',
  colourSuccessBg: 'Onnistumisen tausta',
  colourWarningBg: 'Varoituksen tausta',
  colourDangerBg: 'Vaaran tausta',
  colourSeries: 'Sarja {n}',
  formatUnsupported: 'Tätä numeromuotoa ei voi muuttaa täällä.',
  colourNone: 'Ei väriä',
  formatScalarNative: 'alkuperäinen muoto',
  imageFit: 'Sovitus',
  imageRatio: 'Kuvasuhde',
  fitContain: 'Sovita',
  fitCover: 'Täytä',
  ratioIntrinsic: 'Alkuperäinen',
  fitCoverUnsafe: 'Tätä kuvaa ei voi rajata.',
  imageNotReady: 'Sovitusta voi muuttaa, kun kuva on valmis.',
};

const fr: Record<InspectCopyKey, string> = {
  fieldContent: 'Contenu',
  inspectNodeParagraph: 'Paragraphe',
  inspectNodeHeading: 'Titre',
  inspectNodeList: 'Liste',
  viewOnly: 'Vous pouvez voir ceci, mais pas le modifier.',
  undoFailed: 'Impossible d’annuler. Votre modification est toujours enregistrée.',
  undoConflict:
    'Ceci a changé après votre modification, il n’est donc pas possible de l’annuler ici.',

  toggle: 'Inspecter',
  hintTouch: 'Touchez un élément pour le sélectionner.',
  addAnother: 'En ajouter un autre',
  element: 'Élément',
  region: 'Zone',
  pickerLabel: 'Choisir quoi sélectionner',
  selectParent: 'Sélectionner le parent',
  mention: 'Mentionner',
  remove: 'Retirer {label}',
  selectedCount: '{n} sélectionnés',
  limit:
    'Vous pouvez mentionner jusqu’à {limit} éléments à la fois. Retirez-en un pour en ajouter un autre.',
  stale: 'Modifié. Sélectionnez-le à nouveau.',
  revoked: 'N’est plus disponible',
  expired: 'Expiré. Sélectionnez-le à nouveau.',
  details: 'Détails',
  hideDetails: 'Masquer les détails',
  source: 'Source',
  sourceHidden: 'Masqué pour vos droits d’accès',
  sourceUnmapped: 'Non relié à la source',
  data: 'Données',
  dataUnmapped: 'Source de données non reliée',
  picture: 'Image',
  pictureReplay: 'D’un nouveau rendu de cette version',
  pending: 'Chargement…',
  targetsLabel: 'Éléments que vous pouvez désigner',
  text: 'Texte',
  colour: 'Couleur',
  spacing: 'Espacement',
  numberFormat: 'Format des nombres',
  mixed: 'Mixte',
  apply: 'Appliquer',
  cancel: 'Annuler',
  applying: 'Enregistrement…',
  saved: 'Enregistré dans le brouillon',
  undo: 'Annuler la modification',
  change: '{before} → {after}',
  refused: 'Ceci ne peut pas être modifié directement ici.',
  handoff: 'Modifier dans le chat',
  staleApply: 'Cela a changé pendant votre modification. Sélectionnez-le à nouveau.',
  failed: 'Échec de l’enregistrement. La version précédente est inchangée.',
  hintBar: 'Cliquez sur un élément pour le mentionner · Échap pour quitter',
  where: 'Emplacement',
  shows: 'Affiche',
  changesHere: 'Modifiable ici',
  showSource: 'Afficher où c’est défini',
  hideSource: 'Masquer où c’est défini',
  cropUnavailable: 'Aucun aperçu recadré pour cette vue',
  fromData: 'Provient des données, ne peut donc pas être remplacé',
  noModel: 'Aucun modèle n’est utilisé. Enregistre un nouveau brouillon.',
  savedVersion: 'Enregistré dans le brouillon {version}',
  checkBeforePublish: 'Vérifiez à nouveau avant de publier',
  staleSubject: '{subject} a changé. Sélectionnez à nouveau.',
  staleGeneric: 'Cela a changé. Sélectionnez à nouveau.',
  shownAs: 'Afficher en',
  shownNumber: 'Nombre',
  shownPercent: 'Pourcentage',
  decimals: 'Décimales',
  units: 'Unité',
  unitKr: 'kr',
  unitThousandKr: 'milliers de kr',
  nowSuffix: '{value} actuel',
  selectedToggle: '{n} sélectionnés',
  grouping: 'Séparateur de milliers',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Sélectionnez-le à nouveau pour le modifier',
  colourInk: 'Encre',
  colourMuted: 'Atténué',
  colourBrand: 'Accent',
  colourSuccess: 'Succès',
  colourWarning: 'Avertissement',
  colourDanger: 'Danger',
  saveUncertain: 'Cela a peut-être été enregistré. Nous n’avons pas encore pu le confirmer.',
  checkAgain: 'Vérifier à nouveau',
  margin: 'Marge',
  padding: 'Marge intérieure',
  gap: 'Espacement',
  rowGap: 'Espacement des lignes',
  columnGap: 'Espacement des colonnes',
  sideTop: 'Haut',
  sideRight: 'Droite',
  sideBottom: 'Bas',
  sideLeft: 'Gauche',
  sameOnAllSides: 'Identique sur tous les côtés',
  custom: 'Personnalisé…',
  customPx: 'Taille en px',
  smaller: 'Plus petit',
  larger: 'Plus grand',
  pxValue: '{n} px',
  defaultValue: 'Par défaut ({value})',
  channelText: 'Texte',
  channelBackground: 'Arrière-plan',
  channelBorder: 'Bordure',
  channelFill: 'Remplissage',
  channelStroke: 'Contour',
  chooseColour: 'Choisir la couleur pour {channel}',
  reset: 'Réinitialiser',
  colourPaper: 'Papier',
  colourSurface: 'Surface',
  colourSoft: 'Doux',
  colourLine: 'Ligne',
  colourLineStrong: 'Ligne marquée',
  colourSuccessBg: 'Arrière-plan de succès',
  colourWarningBg: 'Arrière-plan d’avertissement',
  colourDangerBg: 'Arrière-plan de danger',
  colourSeries: 'Série {n}',
  formatUnsupported: 'Ce format de nombre ne peut pas être modifié ici.',
  colourNone: 'Aucune couleur',
  formatScalarNative: 'format d’origine',
  imageFit: 'Ajustement',
  imageRatio: 'Proportions',
  fitContain: 'Contenir',
  fitCover: 'Couvrir',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Cette image ne peut pas être recadrée.',
  imageNotReady: 'L’ajustement pourra changer quand l’image sera prête.',
};

const ga: Record<InspectCopyKey, string> = {
  fieldContent: 'Ábhar',
  inspectNodeParagraph: 'Alt',
  inspectNodeHeading: 'Ceannteideal',
  inspectNodeList: 'Liosta',
  viewOnly: 'Is féidir leat é seo a fheiceáil, ach ní féidir leat é a athrú.',
  undoFailed: 'Níorbh fhéidir é a chealú. Tá d’athrú fós sábháilte.',
  undoConflict: 'Athraíodh é seo tar éis d’eagarthóireachta, mar sin ní féidir é a chealú anseo.',

  toggle: 'Iniúch',
  hintTouch: 'Tapáil ar chuid chun í a roghnú.',
  addAnother: 'Cuir ceann eile leis',
  element: 'Eilimint',
  region: 'Réigiún',
  pickerLabel: 'Roghnaigh cad atá le roghnú',
  selectParent: 'Roghnaigh an tuismitheoir',
  mention: 'Luaigh',
  remove: 'Bain {label}',
  selectedCount: '{n} roghnaithe',
  limit:
    'Is féidir suas le {limit} cuid a lua ag an am céanna. Bain ceann acu chun ceann eile a chur leis.',
  stale: 'Athraithe. Roghnaigh arís é.',
  revoked: 'Níl sé ar fáil a thuilleadh',
  expired: 'Imithe in éag. Roghnaigh arís é.',
  details: 'Sonraí',
  hideDetails: 'Folaigh sonraí',
  source: 'Foinse',
  sourceHidden: 'Folaithe de réir do rochtana',
  sourceUnmapped: 'Gan nasc leis an bhfoinse',
  data: 'Sonraí',
  dataUnmapped: 'Níl an fhoinse sonraí nasctha',
  picture: 'Pictiúr',
  pictureReplay: 'Ó rindreáil úr den leagan seo',
  pending: 'Á lódáil…',
  targetsLabel: 'Codanna ar féidir leat díriú orthu',
  text: 'Téacs',
  colour: 'Dath',
  spacing: 'Spásáil',
  numberFormat: 'Formáid uimhreach',
  mixed: 'Measctha',
  apply: 'Cuir i bhfeidhm',
  cancel: 'Cealaigh',
  applying: 'Á shábháil…',
  saved: 'Sábháilte sa dréacht',
  undo: 'Cealaigh an t-athrú',
  change: '{before} → {after}',
  refused: 'Ní féidir é seo a athrú go díreach anseo.',
  handoff: 'Athraigh é seo sa chomhrá',
  staleApply: 'D’athraigh sé agus tú ag eagarthóireacht. Roghnaigh arís é.',
  failed: 'Níorbh fhéidir sábháil. Tá an leagan roimhe seo gan athrú.',
  hintBar: 'Cliceáil ar chuid chun í a lua · Esc chun imeacht',
  where: 'Cá háit',
  shows: 'Taispeánann',
  changesHere: 'Athraíonn anseo',
  showSource: 'Taispeáin cá sainmhínítear é',
  hideSource: 'Folaigh cá sainmhínítear é',
  cropUnavailable: 'Níl bearradh ar fáil don radharc seo',
  fromData: 'Tagann sé ó shonraí, mar sin ní féidir clóscríobh air',
  noModel: 'Ní úsáidtear aon mhúnla. Sábhálann sé dréacht nua.',
  savedVersion: 'Sábháilte sa dréacht {version}',
  checkBeforePublish: 'Seiceáil arís roimh fhoilsiú',
  staleSubject: 'D’athraigh {subject}. Roghnaigh arís.',
  staleGeneric: 'D’athraigh sé. Roghnaigh arís.',
  shownAs: 'Taispeáin mar',
  shownNumber: 'Uimhir',
  shownPercent: 'Céatadán',
  decimals: 'Deachúlacha',
  units: 'Aonad',
  unitKr: 'kr',
  unitThousandKr: 'míle kr',
  nowSuffix: '{value} anois',
  selectedToggle: '{n} roghnaithe',
  grouping: 'Grúpáil',
  groupOn: '1,000',
  groupOff: '1000',
  staleSelect: 'Roghnaigh arís é chun é a athrú',
  colourInk: 'Dúch',
  colourMuted: 'Maolaithe',
  colourBrand: 'Aiceann',
  colourSuccess: 'Rath',
  colourWarning: 'Rabhadh',
  colourDanger: 'Contúirt',
  saveUncertain: 'B’fhéidir gur sábháladh é seo. Níorbh fhéidir linn é a dheimhniú fós.',
  checkAgain: 'Seiceáil arís',
  margin: 'Imeall',
  padding: 'Stuáil',
  gap: 'Bearna',
  rowGap: 'Bearna idir sraitheanna',
  columnGap: 'Bearna idir colúin',
  sideTop: 'Barr',
  sideRight: 'Deas',
  sideBottom: 'Bun',
  sideLeft: 'Clé',
  sameOnAllSides: 'Mar an gcéanna ar gach taobh',
  custom: 'Saincheaptha…',
  customPx: 'Méid i bpx',
  smaller: 'Níos lú',
  larger: 'Níos mó',
  pxValue: '{n} px',
  defaultValue: 'Réamhshocrú ({value})',
  channelText: 'Téacs',
  channelBackground: 'Cúlra',
  channelBorder: 'Imlíne',
  channelFill: 'Líonadh',
  channelStroke: 'Stríoc',
  chooseColour: 'Roghnaigh dath do {channel}',
  reset: 'Athshocraigh',
  colourPaper: 'Páipéar',
  colourSurface: 'Dromchla',
  colourSoft: 'Bog',
  colourLine: 'Líne',
  colourLineStrong: 'Líne láidir',
  colourSuccessBg: 'Cúlra ratha',
  colourWarningBg: 'Cúlra rabhaidh',
  colourDangerBg: 'Cúlra contúirte',
  colourSeries: 'Sraith {n}',
  formatUnsupported: 'Ní féidir an fhormáid uimhreach seo a athrú anseo.',
  colourNone: 'Gan dath',
  formatScalarNative: 'formáid bhunaidh',
  imageFit: 'Oiriúint',
  imageRatio: 'Cóimheas',
  fitContain: 'Coinnigh iomlán',
  fitCover: 'Clúdaigh',
  ratioIntrinsic: 'Bunaidh',
  fitCoverUnsafe: 'Ní féidir an íomhá seo a bhearradh.',
  imageNotReady: 'Is féidir an oiriúint a athrú nuair a bheidh an íomhá réidh.',
};

const hr: Record<InspectCopyKey, string> = {
  fieldContent: 'Sadržaj',
  inspectNodeParagraph: 'Odlomak',
  inspectNodeHeading: 'Naslov',
  inspectNodeList: 'Popis',
  viewOnly: 'Ovo možete pregledati, ali ne i promijeniti.',
  undoFailed: 'Poništavanje nije uspjelo. Vaša je promjena i dalje spremljena.',
  undoConflict: 'Ovo se promijenilo nakon vaše izmjene pa se ovdje ne može poništiti.',

  toggle: 'Pregledaj',
  hintTouch: 'Dodirnite dio da biste ga odabrali.',
  addAnother: 'Dodaj još jedan',
  element: 'Element',
  region: 'Područje',
  pickerLabel: 'Odaberite što označiti',
  selectParent: 'Odaberi nadređeni',
  mention: 'Spomeni',
  remove: 'Ukloni {label}',
  selectedCount: 'Odabrano: {n}',
  limit:
    'Odjednom možete spomenuti najviše {limit} dijelova. Uklonite jedan da biste dodali drugi.',
  stale: 'Promijenjeno. Odaberite ponovno.',
  revoked: 'Više nije dostupno',
  expired: 'Isteklo. Odaberite ponovno.',
  details: 'Pojedinosti',
  hideDetails: 'Sakrij pojedinosti',
  source: 'Izvor',
  sourceHidden: 'Skriveno za vaš pristup',
  sourceUnmapped: 'Nije povezano s izvorom',
  data: 'Podaci',
  dataUnmapped: 'Izvor podataka nije povezan',
  picture: 'Slika',
  pictureReplay: 'Iz novog prikaza ove verzije',
  pending: 'Učitavanje…',
  targetsLabel: 'Dijelovi koje možete pokazati',
  text: 'Tekst',
  colour: 'Boja',
  spacing: 'Razmak',
  numberFormat: 'Format brojeva',
  mixed: 'Mješovito',
  apply: 'Primijeni',
  cancel: 'Odustani',
  applying: 'Spremanje…',
  saved: 'Spremljeno u skicu',
  undo: 'Poništi',
  change: '{before} → {after}',
  refused: 'Ovo se ne može izravno promijeniti ovdje.',
  handoff: 'Promijenite to u chatu',
  staleApply: 'Promijenilo se dok ste uređivali. Odaberite ponovno.',
  failed: 'Spremanje nije uspjelo. Prethodna verzija je nepromijenjena.',
  hintBar: 'Kliknite dio da biste ga spomenuli · Esc za izlaz',
  where: 'Gdje',
  shows: 'Prikazuje',
  changesHere: 'Mijenja se ovdje',
  showSource: 'Prikaži gdje je definirano',
  hideSource: 'Sakrij gdje je definirano',
  cropUnavailable: 'Nema isječka za ovaj prikaz',
  fromData: 'Dolazi iz podataka pa se ne može prepisati',
  noModel: 'Ne koristi se nijedan model. Sprema novu skicu.',
  savedVersion: 'Spremljeno u skicu {version}',
  checkBeforePublish: 'Provjerite ponovno prije objave',
  staleSubject: '{subject}: promijenjeno. Odaberite ponovno.',
  staleGeneric: 'Promijenilo se. Odaberite ponovno.',
  shownAs: 'Prikaži kao',
  shownNumber: 'Broj',
  shownPercent: 'Postotak',
  decimals: 'Decimale',
  units: 'Jedinica',
  unitKr: 'kr',
  unitThousandKr: 'tisuće kr',
  nowSuffix: '{value} trenutačno',
  selectedToggle: 'Odabrano: {n}',
  grouping: 'Grupiranje znamenki',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Odaberite ponovno da biste promijenili',
  colourInk: 'Tinta',
  colourMuted: 'Prigušeno',
  colourBrand: 'Naglasak',
  colourSuccess: 'Uspjeh',
  colourWarning: 'Upozorenje',
  colourDanger: 'Opasnost',
  saveUncertain: 'Ovo je možda spremljeno. Još to nismo mogli potvrditi.',
  checkAgain: 'Provjeri ponovno',
  margin: 'Vanjski razmak',
  padding: 'Unutarnji razmak',
  gap: 'Razmak',
  rowGap: 'Razmak redaka',
  columnGap: 'Razmak stupaca',
  sideTop: 'Gore',
  sideRight: 'Desno',
  sideBottom: 'Dolje',
  sideLeft: 'Lijevo',
  sameOnAllSides: 'Isto na svim stranama',
  custom: 'Prilagođeno…',
  customPx: 'Veličina u px',
  smaller: 'Manje',
  larger: 'Veće',
  pxValue: '{n} px',
  defaultValue: 'Zadano ({value})',
  channelText: 'Tekst',
  channelBackground: 'Pozadina',
  channelBorder: 'Obrub',
  channelFill: 'Ispuna',
  channelStroke: 'Potez',
  chooseColour: 'Odaberite boju za {channel}',
  reset: 'Vrati',
  colourPaper: 'Papir',
  colourSurface: 'Površina',
  colourSoft: 'Meko',
  colourLine: 'Linija',
  colourLineStrong: 'Izražena linija',
  colourSuccessBg: 'Pozadina uspjeha',
  colourWarningBg: 'Pozadina upozorenja',
  colourDangerBg: 'Pozadina opasnosti',
  colourSeries: 'Niz {n}',
  formatUnsupported: 'Ovaj format broja ne može se ovdje promijeniti.',
  colourNone: 'Bez boje',
  formatScalarNative: 'izvorni format',
  imageFit: 'Prilagodba',
  imageRatio: 'Omjer',
  fitContain: 'Cijela slika',
  fitCover: 'Ispuni',
  ratioIntrinsic: 'Izvorno',
  fitCoverUnsafe: 'Ova se slika ne može obrezati.',
  imageNotReady: 'Prilagodba se može promijeniti kad slika bude spremna.',
};

const hu: Record<InspectCopyKey, string> = {
  fieldContent: 'Tartalom',
  inspectNodeParagraph: 'Bekezdés',
  inspectNodeHeading: 'Címsor',
  inspectNodeList: 'Lista',
  viewOnly: 'Ezt megtekintheti, de nem módosíthatja.',
  undoFailed: 'A visszavonás nem sikerült. A módosítása továbbra is mentve van.',
  undoConflict: 'Ez a szerkesztése után megváltozott, ezért itt nem vonható vissza.',

  toggle: 'Vizsgálat',
  hintTouch: 'Koppintson egy részre a kijelöléséhez.',
  addAnother: 'Újabb hozzáadása',
  element: 'Elem',
  region: 'Terület',
  pickerLabel: 'Válassza ki, mit jelöl ki',
  selectParent: 'Szülőelem kijelölése',
  mention: 'Említés',
  remove: '{label} eltávolítása',
  selectedCount: '{n} kijelölve',
  limit: 'Egyszerre legfeljebb {limit} részt említhet. Távolítson el egyet, hogy újat adjon hozzá.',
  stale: 'Megváltozott. Jelölje ki újra.',
  revoked: 'Már nem érhető el',
  expired: 'Lejárt. Jelölje ki újra.',
  details: 'Részletek',
  hideDetails: 'Részletek elrejtése',
  source: 'Forrás',
  sourceHidden: 'Az Ön hozzáférése számára rejtett',
  sourceUnmapped: 'Nincs a forráshoz kötve',
  data: 'Adatok',
  dataUnmapped: 'Az adatforrás nincs hozzákötve',
  picture: 'Kép',
  pictureReplay: 'E verzió új megjelenítéséből',
  pending: 'Betöltés…',
  targetsLabel: 'Részek, amelyekre rámutathat',
  text: 'Szöveg',
  colour: 'Szín',
  spacing: 'Térköz',
  numberFormat: 'Számformátum',
  mixed: 'Vegyes',
  apply: 'Alkalmaz',
  cancel: 'Mégse',
  applying: 'Mentés…',
  saved: 'Mentve a piszkozatba',
  undo: 'Visszavonás',
  change: '{before} → {after}',
  refused: 'Ez itt közvetlenül nem módosítható.',
  handoff: 'Módosítsa a csevegésben',
  staleApply: 'Szerkesztés közben megváltozott. Jelölje ki újra.',
  failed: 'A mentés nem sikerült. Az előző verzió változatlan.',
  hintBar: 'Kattintson egy részre az említéséhez · Esc a kilépéshez',
  where: 'Hol',
  shows: 'Megjeleníti',
  changesHere: 'Itt módosul',
  showSource: 'Megmutatja, hol van meghatározva',
  hideSource: 'Elrejti, hol van meghatározva',
  cropUnavailable: 'Ehhez a nézethez nincs kivágás',
  fromData: 'Adatokból származik, ezért nem írható felül',
  noModel: 'Nem használ modellt. Új piszkozatot ment.',
  savedVersion: 'Mentve a(z) {version} piszkozatba',
  checkBeforePublish: 'Közzététel előtt ellenőrizze újra',
  staleSubject: '{subject} megváltozott. Jelölje ki újra.',
  staleGeneric: 'Megváltozott. Jelölje ki újra.',
  shownAs: 'Megjelenítés',
  shownNumber: 'Szám',
  shownPercent: 'Százalék',
  decimals: 'Tizedesjegyek',
  units: 'Mértékegység',
  unitKr: 'kr',
  unitThousandKr: 'ezer kr',
  nowSuffix: '{value} most',
  selectedToggle: '{n} kijelölve',
  grouping: 'Ezres tagolás',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'A módosításhoz jelölje ki újra',
  colourInk: 'Tinta',
  colourMuted: 'Tompított',
  colourBrand: 'Kiemelés',
  colourSuccess: 'Siker',
  colourWarning: 'Figyelmeztetés',
  colourDanger: 'Veszély',
  saveUncertain: 'Lehet, hogy mentve lett. Még nem tudtuk megerősíteni.',
  checkAgain: 'Ellenőrzés újra',
  margin: 'Külső margó',
  padding: 'Belső margó',
  gap: 'Térköz',
  rowGap: 'Sorköz',
  columnGap: 'Oszlopköz',
  sideTop: 'Fent',
  sideRight: 'Jobbra',
  sideBottom: 'Lent',
  sideLeft: 'Balra',
  sameOnAllSides: 'Minden oldalon azonos',
  custom: 'Egyéni…',
  customPx: 'Méret px-ben',
  smaller: 'Kisebb',
  larger: 'Nagyobb',
  pxValue: '{n} px',
  defaultValue: 'Alapértelmezett ({value})',
  channelText: 'Szöveg',
  channelBackground: 'Háttér',
  channelBorder: 'Szegély',
  channelFill: 'Kitöltés',
  channelStroke: 'Körvonal',
  chooseColour: 'Szín kiválasztása: {channel}',
  reset: 'Visszaállítás',
  colourPaper: 'Papír',
  colourSurface: 'Felület',
  colourSoft: 'Lágy',
  colourLine: 'Vonal',
  colourLineStrong: 'Erős vonal',
  colourSuccessBg: 'Siker háttere',
  colourWarningBg: 'Figyelmeztetés háttere',
  colourDangerBg: 'Veszély háttere',
  colourSeries: '{n}. sorozat',
  formatUnsupported: 'Ez a számformátum itt nem módosítható.',
  colourNone: 'Nincs szín',
  formatScalarNative: 'eredeti formátum',
  imageFit: 'Illesztés',
  imageRatio: 'Képarány',
  fitContain: 'Teljes kép',
  fitCover: 'Kitöltés',
  ratioIntrinsic: 'Eredeti',
  fitCoverUnsafe: 'Ez a kép nem vágható.',
  imageNotReady: 'Az illesztés akkor módosítható, ha a kép elkészült.',
};

const it: Record<InspectCopyKey, string> = {
  fieldContent: 'Contenuto',
  inspectNodeParagraph: 'Paragrafo',
  inspectNodeHeading: 'Intestazione',
  inspectNodeList: 'Elenco',
  viewOnly: 'Puoi vedere questo elemento, ma non modificarlo.',
  undoFailed: 'Impossibile annullare. La tua modifica è ancora salvata.',
  undoConflict: 'È cambiato dopo la tua modifica, quindi non si può annullare qui.',

  toggle: 'Ispeziona',
  hintTouch: 'Tocca una parte per selezionarla.',
  addAnother: 'Aggiungi un’altra',
  element: 'Elemento',
  region: 'Area',
  pickerLabel: 'Scegli cosa selezionare',
  selectParent: 'Seleziona il contenitore',
  mention: 'Menziona',
  remove: 'Rimuovi {label}',
  selectedCount: '{n} selezionate',
  limit: 'Puoi menzionare fino a {limit} parti alla volta. Rimuovine una per aggiungerne un’altra.',
  stale: 'Modificato. Selezionalo di nuovo.',
  revoked: 'Non più disponibile',
  expired: 'Scaduto. Selezionalo di nuovo.',
  details: 'Dettagli',
  hideDetails: 'Nascondi dettagli',
  source: 'Origine',
  sourceHidden: 'Nascosto per il tuo livello di accesso',
  sourceUnmapped: 'Non collegato all’origine',
  data: 'Dati',
  dataUnmapped: 'Origine dati non collegata',
  picture: 'Immagine',
  pictureReplay: 'Da un nuovo rendering di questa versione',
  pending: 'Caricamento…',
  targetsLabel: 'Parti che puoi indicare',
  text: 'Testo',
  colour: 'Colore',
  spacing: 'Spaziatura',
  numberFormat: 'Formato numerico',
  mixed: 'Misto',
  apply: 'Applica',
  cancel: 'Annulla',
  applying: 'Salvataggio…',
  saved: 'Salvato nella bozza',
  undo: 'Annulla modifica',
  change: '{before} → {after}',
  refused: 'Questo non si può modificare direttamente qui.',
  handoff: 'Modificalo nella chat',
  staleApply: 'È cambiato mentre lo modificavi. Selezionalo di nuovo.',
  failed: 'Salvataggio non riuscito. La versione precedente è invariata.',
  hintBar: 'Fai clic su una parte per menzionarla · Esc per uscire',
  where: 'Dove',
  shows: 'Mostra',
  changesHere: 'Modifiche qui',
  showSource: 'Mostra dove è definito',
  hideSource: 'Nascondi dove è definito',
  cropUnavailable: 'Nessun ritaglio per questa vista',
  fromData: 'Viene dai dati, quindi non si può sovrascrivere',
  noModel: 'Non viene usato alcun modello. Salva una nuova bozza.',
  savedVersion: 'Salvato nella bozza {version}',
  checkBeforePublish: 'Ricontrolla prima di pubblicare',
  staleSubject: '{subject} è cambiato. Seleziona di nuovo.',
  staleGeneric: 'È cambiato. Seleziona di nuovo.',
  shownAs: 'Mostra come',
  shownNumber: 'Numero',
  shownPercent: 'Percentuale',
  decimals: 'Decimali',
  units: 'Unità',
  unitKr: 'kr',
  unitThousandKr: 'migliaia di kr',
  nowSuffix: '{value} attuale',
  selectedToggle: '{n} selezionate',
  grouping: 'Separatore delle migliaia',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Selezionalo di nuovo per modificarlo',
  colourInk: 'Inchiostro',
  colourMuted: 'Attenuato',
  colourBrand: 'Accento',
  colourSuccess: 'Successo',
  colourWarning: 'Avviso',
  colourDanger: 'Pericolo',
  saveUncertain: 'Potrebbe essere stato salvato. Non abbiamo ancora potuto confermarlo.',
  checkAgain: 'Controlla di nuovo',
  margin: 'Margine',
  padding: 'Spaziatura interna',
  gap: 'Spazio',
  rowGap: 'Spazio tra le righe',
  columnGap: 'Spazio tra le colonne',
  sideTop: 'In alto',
  sideRight: 'A destra',
  sideBottom: 'In basso',
  sideLeft: 'A sinistra',
  sameOnAllSides: 'Uguale su tutti i lati',
  custom: 'Personalizzato…',
  customPx: 'Dimensione in px',
  smaller: 'Più piccolo',
  larger: 'Più grande',
  pxValue: '{n} px',
  defaultValue: 'Predefinito ({value})',
  channelText: 'Testo',
  channelBackground: 'Sfondo',
  channelBorder: 'Bordo',
  channelFill: 'Riempimento',
  channelStroke: 'Tratto',
  chooseColour: 'Scegli il colore per {channel}',
  reset: 'Ripristina',
  colourPaper: 'Carta',
  colourSurface: 'Superficie',
  colourSoft: 'Tenue',
  colourLine: 'Linea',
  colourLineStrong: 'Linea marcata',
  colourSuccessBg: 'Sfondo di successo',
  colourWarningBg: 'Sfondo di avviso',
  colourDangerBg: 'Sfondo di pericolo',
  colourSeries: 'Serie {n}',
  formatUnsupported: 'Questo formato numerico non può essere modificato qui.',
  colourNone: 'Nessun colore',
  formatScalarNative: 'formato originale',
  imageFit: 'Adattamento',
  imageRatio: 'Proporzioni',
  fitContain: 'Contieni',
  fitCover: 'Riempi',
  ratioIntrinsic: 'Originale',
  fitCoverUnsafe: 'Questa immagine non può essere ritagliata.',
  imageNotReady: 'L’adattamento si può cambiare quando l’immagine è pronta.',
};

const lt: Record<InspectCopyKey, string> = {
  fieldContent: 'Turinys',
  inspectNodeParagraph: 'Pastraipa',
  inspectNodeHeading: 'Antraštė',
  inspectNodeList: 'Sąrašas',
  viewOnly: 'Tai galite peržiūrėti, bet negalite pakeisti.',
  undoFailed: 'Nepavyko anuliuoti. Jūsų pakeitimas vis dar išsaugotas.',
  undoConflict: 'Po jūsų pakeitimo tai pasikeitė, todėl čia to anuliuoti negalima.',

  toggle: 'Tikrinti',
  hintTouch: 'Bakstelėkite dalį, kad ją pasirinktumėte.',
  addAnother: 'Pridėti dar vieną',
  element: 'Elementas',
  region: 'Sritis',
  pickerLabel: 'Pasirinkite, ką žymėti',
  selectParent: 'Pasirinkti tėvinį',
  mention: 'Paminėti',
  remove: 'Pašalinti {label}',
  selectedCount: 'Pasirinkta: {n}',
  limit: 'Vienu metu galite paminėti iki {limit} dalių. Pašalinkite vieną, kad pridėtumėte kitą.',
  stale: 'Pakeista. Pasirinkite dar kartą.',
  revoked: 'Nebepasiekiama',
  expired: 'Nebegalioja. Pasirinkite dar kartą.',
  details: 'Išsamiau',
  hideDetails: 'Slėpti išsamią informaciją',
  source: 'Šaltinis',
  sourceHidden: 'Paslėpta dėl jūsų prieigos',
  sourceUnmapped: 'Nesusieta su šaltiniu',
  data: 'Duomenys',
  dataUnmapped: 'Duomenų šaltinis nesusietas',
  picture: 'Vaizdas',
  pictureReplay: 'Iš naujo šios versijos atvaizdavimo',
  pending: 'Įkeliama…',
  targetsLabel: 'Dalys, kurias galite nurodyti',
  text: 'Tekstas',
  colour: 'Spalva',
  spacing: 'Tarpai',
  numberFormat: 'Skaičių formatas',
  mixed: 'Mišrus',
  apply: 'Taikyti',
  cancel: 'Atšaukti',
  applying: 'Išsaugoma…',
  saved: 'Išsaugota juodraštyje',
  undo: 'Anuliuoti',
  change: '{before} → {after}',
  refused: 'Čia to negalima pakeisti tiesiogiai.',
  handoff: 'Pakeiskite tai pokalbyje',
  staleApply: 'Pasikeitė, kol redagavote. Pasirinkite dar kartą.',
  failed: 'Nepavyko išsaugoti. Ankstesnė versija nepakeista.',
  hintBar: 'Spustelėkite dalį, kad ją paminėtumėte · Esc – išeiti',
  where: 'Kur',
  shows: 'Rodo',
  changesHere: 'Keičiama čia',
  showSource: 'Rodyti, kur apibrėžta',
  hideSource: 'Slėpti, kur apibrėžta',
  cropUnavailable: 'Šiam rodiniui iškarpos nėra',
  fromData: 'Gaunama iš duomenų, todėl negalima perrašyti',
  noModel: 'Nenaudojamas joks modelis. Išsaugo naują juodraštį.',
  savedVersion: 'Išsaugota juodraštyje {version}',
  checkBeforePublish: 'Prieš skelbdami patikrinkite dar kartą',
  staleSubject: '{subject}: pakeista. Pasirinkite dar kartą.',
  staleGeneric: 'Pasikeitė. Pasirinkite dar kartą.',
  shownAs: 'Rodyti kaip',
  shownNumber: 'Skaičius',
  shownPercent: 'Procentai',
  decimals: 'Dešimtainės vietos',
  units: 'Vienetai',
  unitKr: 'kr',
  unitThousandKr: 'tūkst. kr',
  nowSuffix: '{value} dabar',
  selectedToggle: 'Pasirinkta: {n}',
  grouping: 'Tūkstančių skyriklis',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Pasirinkite dar kartą, kad pakeistumėte',
  colourInk: 'Rašalas',
  colourMuted: 'Prislopinta',
  colourBrand: 'Akcentas',
  colourSuccess: 'Sėkmė',
  colourWarning: 'Įspėjimas',
  colourDanger: 'Pavojus',
  saveUncertain: 'Tai galėjo būti išsaugota. Kol kas negalėjome patvirtinti.',
  checkAgain: 'Tikrinti dar kartą',
  margin: 'Išorinė paraštė',
  padding: 'Vidinė paraštė',
  gap: 'Tarpas',
  rowGap: 'Tarpas tarp eilučių',
  columnGap: 'Tarpas tarp stulpelių',
  sideTop: 'Viršuje',
  sideRight: 'Dešinėje',
  sideBottom: 'Apačioje',
  sideLeft: 'Kairėje',
  sameOnAllSides: 'Vienodai visose pusėse',
  custom: 'Pasirinktinis…',
  customPx: 'Dydis px',
  smaller: 'Mažesnis',
  larger: 'Didesnis',
  pxValue: '{n} px',
  defaultValue: 'Numatytasis ({value})',
  channelText: 'Tekstas',
  channelBackground: 'Fonas',
  channelBorder: 'Kraštinė',
  channelFill: 'Užpildas',
  channelStroke: 'Brūkšnys',
  chooseColour: 'Pasirinkite spalvą: {channel}',
  reset: 'Atkurti',
  colourPaper: 'Popierius',
  colourSurface: 'Paviršius',
  colourSoft: 'Švelni',
  colourLine: 'Linija',
  colourLineStrong: 'Ryški linija',
  colourSuccessBg: 'Sėkmės fonas',
  colourWarningBg: 'Įspėjimo fonas',
  colourDangerBg: 'Pavojaus fonas',
  colourSeries: 'Serija {n}',
  formatUnsupported: 'Šio skaičių formato čia pakeisti negalima.',
  colourNone: 'Be spalvos',
  formatScalarNative: 'pradinis formatas',
  imageFit: 'Pritaikymas',
  imageRatio: 'Santykis',
  fitContain: 'Visas vaizdas',
  fitCover: 'Užpildyti',
  ratioIntrinsic: 'Originalus',
  fitCoverUnsafe: 'Šio vaizdo negalima apkarpyti.',
  imageNotReady: 'Pritaikymą galima keisti, kai vaizdas bus paruoštas.',
};

const lv: Record<InspectCopyKey, string> = {
  fieldContent: 'Saturs',
  inspectNodeParagraph: 'Rindkopa',
  inspectNodeHeading: 'Virsraksts',
  inspectNodeList: 'Saraksts',
  viewOnly: 'Jūs varat to skatīt, bet nevarat mainīt.',
  undoFailed: 'Neizdevās atsaukt. Jūsu izmaiņas joprojām ir saglabātas.',
  undoConflict: 'Pēc jūsu labojuma tas ir mainījies, tāpēc šeit to nevar atsaukt.',

  toggle: 'Pārbaudīt',
  hintTouch: 'Pieskarieties daļai, lai to atlasītu.',
  addAnother: 'Pievienot vēl vienu',
  element: 'Elements',
  region: 'Apgabals',
  pickerLabel: 'Izvēlieties, ko atlasīt',
  selectParent: 'Atlasīt vecākelementu',
  mention: 'Pieminēt',
  remove: 'Noņemt {label}',
  selectedCount: 'Atlasīti: {n}',
  limit: 'Vienlaikus varat pieminēt līdz {limit} daļām. Noņemiet vienu, lai pievienotu citu.',
  stale: 'Mainīts. Atlasiet vēlreiz.',
  revoked: 'Vairs nav pieejams',
  expired: 'Beidzies derīgums. Atlasiet vēlreiz.',
  details: 'Sīkāka informācija',
  hideDetails: 'Paslēpt sīkāku informāciju',
  source: 'Avots',
  sourceHidden: 'Paslēpts jūsu piekļuvei',
  sourceUnmapped: 'Nav saistīts ar avotu',
  data: 'Dati',
  dataUnmapped: 'Datu avots nav saistīts',
  picture: 'Attēls',
  pictureReplay: 'No jaunas šīs versijas renderēšanas',
  pending: 'Ielādē…',
  targetsLabel: 'Daļas, uz kurām varat norādīt',
  text: 'Teksts',
  colour: 'Krāsa',
  spacing: 'Atstarpes',
  numberFormat: 'Skaitļu formāts',
  mixed: 'Jaukts',
  apply: 'Lietot',
  cancel: 'Atcelt',
  applying: 'Saglabā…',
  saved: 'Saglabāts melnrakstā',
  undo: 'Atsaukt',
  change: '{before} → {after}',
  refused: 'Šeit to nevar mainīt tieši.',
  handoff: 'Mainiet to tērzēšanā',
  staleApply: 'Tas mainījās, kamēr rediģējāt. Atlasiet vēlreiz.',
  failed: 'Neizdevās saglabāt. Iepriekšējā versija nav mainīta.',
  hintBar: 'Noklikšķiniet uz daļas, lai to pieminētu · Esc, lai izietu',
  where: 'Kur',
  shows: 'Rāda',
  changesHere: 'Mainās šeit',
  showSource: 'Rādīt, kur tas definēts',
  hideSource: 'Paslēpt, kur tas definēts',
  cropUnavailable: 'Šim skatam nav izgriezuma',
  fromData: 'Nāk no datiem, tāpēc to nevar pārrakstīt',
  noModel: 'Netiek izmantots neviens modelis. Saglabā jaunu melnrakstu.',
  savedVersion: 'Saglabāts melnrakstā {version}',
  checkBeforePublish: 'Pirms publicēšanas pārbaudiet vēlreiz',
  staleSubject: '{subject}: mainīts. Atlasiet vēlreiz.',
  staleGeneric: 'Tas mainījās. Atlasiet vēlreiz.',
  shownAs: 'Rādīt kā',
  shownNumber: 'Skaitlis',
  shownPercent: 'Procenti',
  decimals: 'Decimāldaļas',
  units: 'Mērvienība',
  unitKr: 'kr',
  unitThousandKr: 'tūkst. kr',
  nowSuffix: '{value} pašlaik',
  selectedToggle: 'Atlasīti: {n}',
  grouping: 'Tūkstošu atdalītājs',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Atlasiet vēlreiz, lai mainītu',
  colourInk: 'Tinte',
  colourMuted: 'Pieklusināta',
  colourBrand: 'Akcents',
  colourSuccess: 'Veiksme',
  colourWarning: 'Brīdinājums',
  colourDanger: 'Bīstami',
  saveUncertain: 'Iespējams, tas ir saglabāts. Mēs to vēl nevarējām apstiprināt.',
  checkAgain: 'Pārbaudīt vēlreiz',
  margin: 'Ārējā atkāpe',
  padding: 'Iekšējā atkāpe',
  gap: 'Atstarpe',
  rowGap: 'Rindu atstarpe',
  columnGap: 'Kolonnu atstarpe',
  sideTop: 'Augšā',
  sideRight: 'Pa labi',
  sideBottom: 'Apakšā',
  sideLeft: 'Pa kreisi',
  sameOnAllSides: 'Vienādi visās malās',
  custom: 'Pielāgots…',
  customPx: 'Izmērs px',
  smaller: 'Mazāk',
  larger: 'Vairāk',
  pxValue: '{n} px',
  defaultValue: 'Noklusējums ({value})',
  channelText: 'Teksts',
  channelBackground: 'Fons',
  channelBorder: 'Apmale',
  channelFill: 'Aizpildījums',
  channelStroke: 'Līnija',
  chooseColour: 'Izvēlieties krāsu: {channel}',
  reset: 'Atiestatīt',
  colourPaper: 'Papīrs',
  colourSurface: 'Virsma',
  colourSoft: 'Maiga',
  colourLine: 'Līnija',
  colourLineStrong: 'Izteikta līnija',
  colourSuccessBg: 'Veiksmes fons',
  colourWarningBg: 'Brīdinājuma fons',
  colourDangerBg: 'Briesmu fons',
  colourSeries: 'Sērija {n}',
  formatUnsupported: 'Šo skaitļu formātu šeit nevar mainīt.',
  colourNone: 'Bez krāsas',
  formatScalarNative: 'sākotnējais formāts',
  imageFit: 'Pielāgošana',
  imageRatio: 'Attiecība',
  fitContain: 'Viss attēls',
  fitCover: 'Aizpildīt',
  ratioIntrinsic: 'Oriģināls',
  fitCoverUnsafe: 'Šo attēlu nevar apgriezt.',
  imageNotReady: 'Pielāgošanu var mainīt, kad attēls būs gatavs.',
};

const mt: Record<InspectCopyKey, string> = {
  fieldContent: 'Kontenut',
  inspectNodeParagraph: 'Paragrafu',
  inspectNodeHeading: 'Intestatura',
  inspectNodeList: 'Lista',
  viewOnly: 'Tista’ tara dan, imma ma tistax tibdlu.',
  undoFailed: 'Ma setax jitħassar. Il-bidla tiegħek għadha ssejvjata.',
  undoConflict: 'Dan inbidel wara l-editjar tiegħek, allura ma jistax jitħassar hawn.',

  toggle: 'Spezzjona',
  hintTouch: 'Agħfas fuq parti biex tagħżilha.',
  addAnother: 'Żid oħra',
  element: 'Element',
  region: 'Żona',
  pickerLabel: 'Agħżel x’se tagħżel',
  selectParent: 'Agħżel il-ġenitur',
  mention: 'Semmi',
  remove: 'Neħħi {label}',
  selectedCount: '{n} magħżula',
  limit: 'Tista’ ssemmi sa {limit} partijiet f’daqqa. Neħħi waħda biex iżżid oħra.',
  stale: 'Inbidel. Erġa’ agħżlu.',
  revoked: 'M’għadux disponibbli',
  expired: 'Skada. Erġa’ agħżlu.',
  details: 'Dettalji',
  hideDetails: 'Aħbi d-dettalji',
  source: 'Sors',
  sourceHidden: 'Moħbi għall-aċċess tiegħek',
  sourceUnmapped: 'Mhux marbut mas-sors',
  data: 'Data',
  dataUnmapped: 'Is-sors tad-data mhux marbut',
  picture: 'Stampa',
  pictureReplay: 'Minn rendering ġdid ta’ din il-verżjoni',
  pending: 'Qed jitgħabba…',
  targetsLabel: 'Partijiet li tista’ tipponta lejhom',
  text: 'Test',
  colour: 'Kulur',
  spacing: 'Spazjar',
  numberFormat: 'Format tan-numri',
  mixed: 'Imħallat',
  apply: 'Applika',
  cancel: 'Ikkanċella',
  applying: 'Qed jissejvja…',
  saved: 'Issejvjat fl-abbozz',
  undo: 'Erġa’ lura',
  change: '{before} → {after}',
  refused: 'Dan ma jistax jinbidel direttament hawn.',
  handoff: 'Ibdlu fiċ-chat',
  staleApply: 'Inbidel waqt li kont qed teditja. Erġa’ agħżlu.',
  failed: 'Ma setax jissejvja. Il-verżjoni ta’ qabel ma nbidlitx.',
  hintBar: 'Ikklikkja fuq parti biex issemmiha · Esc biex toħroġ',
  where: 'Fejn',
  shows: 'Juri',
  changesHere: 'Jinbidel hawn',
  showSource: 'Uri fejn huwa definit',
  hideSource: 'Aħbi fejn huwa definit',
  cropUnavailable: 'L-ebda qatgħa għal din il-veduta',
  fromData: 'Ġej mid-data, għalhekk ma jistax jinkiteb fuqu',
  noModel: 'L-ebda mudell ma jintuża. Jissejvja abbozz ġdid.',
  savedVersion: 'Issejvjat fl-abbozz {version}',
  checkBeforePublish: 'Iċċekkja mill-ġdid qabel tippubblika',
  staleSubject: '{subject} inbidel. Erġa’ agħżel.',
  staleGeneric: 'Inbidel. Erġa’ agħżel.',
  shownAs: 'Uri bħala',
  shownNumber: 'Numru',
  shownPercent: 'Perċentwal',
  decimals: 'Deċimali',
  units: 'Unità',
  unitKr: 'kr',
  unitThousandKr: 'eluf ta’ kr',
  nowSuffix: '{value} issa',
  selectedToggle: '{n} magħżula',
  grouping: 'Raggruppament',
  groupOn: '1,000',
  groupOff: '1000',
  staleSelect: 'Erġa’ agħżlu biex tibdlu',
  colourInk: 'Linka',
  colourMuted: 'Mrażżan',
  colourBrand: 'Aċċent',
  colourSuccess: 'Suċċess',
  colourWarning: 'Twissija',
  colourDanger: 'Periklu',
  saveUncertain: 'Dan seta’ jkun ġie ssejvjat. Għadna ma stajniex nikkonfermawh.',
  checkAgain: 'Iċċekkja mill-ġdid',
  margin: 'Marġni',
  padding: 'Mili ġewwa',
  gap: 'Spazju',
  rowGap: 'Spazju bejn ir-ringieli',
  columnGap: 'Spazju bejn il-kolonni',
  sideTop: 'Fuq',
  sideRight: 'Lemin',
  sideBottom: 'Isfel',
  sideLeft: 'Xellug',
  sameOnAllSides: 'L-istess fuq il-ġnub kollha',
  custom: 'Personalizzat…',
  customPx: 'Daqs f’px',
  smaller: 'Iżgħar',
  larger: 'Ikbar',
  pxValue: '{n} px',
  defaultValue: 'Default ({value})',
  channelText: 'Test',
  channelBackground: 'Sfond',
  channelBorder: 'Bordura',
  channelFill: 'Mili',
  channelStroke: 'Linja',
  chooseColour: 'Agħżel kulur għal {channel}',
  reset: 'Irrisettja',
  colourPaper: 'Karta',
  colourSurface: 'Wiċċ',
  colourSoft: 'Artab',
  colourLine: 'Linja',
  colourLineStrong: 'Linja qawwija',
  colourSuccessBg: 'Sfond tas-suċċess',
  colourWarningBg: 'Sfond tat-twissija',
  colourDangerBg: 'Sfond tal-periklu',
  colourSeries: 'Serje {n}',
  formatUnsupported: 'Dan il-format tan-numri ma jistax jinbidel hawn.',
  colourNone: 'Ebda kulur',
  formatScalarNative: 'format oriġinali',
  imageFit: 'Tqegħid',
  imageRatio: 'Proporzjon',
  fitContain: 'Kollha',
  fitCover: 'Imla',
  ratioIntrinsic: 'Oriġinali',
  fitCoverUnsafe: 'Din l-istampa ma tistax tinqata’.',
  imageNotReady: 'It-tqegħid jista’ jinbidel ladarba l-istampa tkun lesta.',
};

const nl: Record<InspectCopyKey, string> = {
  fieldContent: 'Inhoud',
  inspectNodeParagraph: 'Alinea',
  inspectNodeHeading: 'Kop',
  inspectNodeList: 'Lijst',
  viewOnly: 'Je kunt dit bekijken, maar niet wijzigen.',
  undoFailed: 'Ongedaan maken is mislukt. Je wijziging is nog steeds opgeslagen.',
  undoConflict: 'Dit is gewijzigd na je bewerking, dus het kan hier niet ongedaan worden gemaakt.',

  toggle: 'Inspecteren',
  hintTouch: 'Tik op een onderdeel om het te selecteren.',
  addAnother: 'Nog een toevoegen',
  element: 'Element',
  region: 'Gebied',
  pickerLabel: 'Kies wat je selecteert',
  selectParent: 'Bovenliggend selecteren',
  mention: 'Noemen',
  remove: '{label} verwijderen',
  selectedCount: '{n} geselecteerd',
  limit:
    'Je kunt tot {limit} onderdelen tegelijk noemen. Verwijder er één om een ander toe te voegen.',
  stale: 'Gewijzigd. Selecteer het opnieuw.',
  revoked: 'Niet meer beschikbaar',
  expired: 'Verlopen. Selecteer het opnieuw.',
  details: 'Details',
  hideDetails: 'Details verbergen',
  source: 'Bron',
  sourceHidden: 'Verborgen voor jouw toegang',
  sourceUnmapped: 'Niet aan de bron gekoppeld',
  data: 'Gegevens',
  dataUnmapped: 'Gegevensbron niet gekoppeld',
  picture: 'Afbeelding',
  pictureReplay: 'Uit een nieuwe weergave van deze versie',
  pending: 'Laden…',
  targetsLabel: 'Onderdelen waar je naar kunt wijzen',
  text: 'Tekst',
  colour: 'Kleur',
  spacing: 'Afstand',
  numberFormat: 'Getalnotatie',
  mixed: 'Gemengd',
  apply: 'Toepassen',
  cancel: 'Annuleren',
  applying: 'Opslaan…',
  saved: 'Opgeslagen in concept',
  undo: 'Ongedaan maken',
  change: '{before} → {after}',
  refused: 'Dit kan hier niet rechtstreeks worden gewijzigd.',
  handoff: 'Wijzig dit in de chat',
  staleApply: 'Het is gewijzigd terwijl je aan het bewerken was. Selecteer het opnieuw.',
  failed: 'Opslaan mislukt. De vorige versie is ongewijzigd.',
  hintBar: 'Klik op een onderdeel om het te noemen · Esc om te stoppen',
  where: 'Waar',
  shows: 'Toont',
  changesHere: 'Wijzigt hier',
  showSource: 'Tonen waar het is gedefinieerd',
  hideSource: 'Verbergen waar het is gedefinieerd',
  cropUnavailable: 'Geen uitsnede voor deze weergave',
  fromData: 'Komt uit gegevens, dus kan niet worden overschreven',
  noModel: 'Er wordt geen model gebruikt. Slaat een nieuw concept op.',
  savedVersion: 'Opgeslagen in concept {version}',
  checkBeforePublish: 'Controleer opnieuw voor publicatie',
  staleSubject: '{subject} is gewijzigd. Selecteer opnieuw.',
  staleGeneric: 'Het is gewijzigd. Selecteer opnieuw.',
  shownAs: 'Weergeven als',
  shownNumber: 'Getal',
  shownPercent: 'Procent',
  decimals: 'Decimalen',
  units: 'Eenheid',
  unitKr: 'kr',
  unitThousandKr: 'duizend kr',
  nowSuffix: '{value} nu',
  selectedToggle: '{n} geselecteerd',
  grouping: 'Groepering',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Selecteer het opnieuw om het te wijzigen',
  colourInk: 'Inkt',
  colourMuted: 'Gedempt',
  colourBrand: 'Accent',
  colourSuccess: 'Geslaagd',
  colourWarning: 'Waarschuwing',
  colourDanger: 'Gevaar',
  saveUncertain: 'Dit is mogelijk opgeslagen. We konden het nog niet bevestigen.',
  checkAgain: 'Opnieuw controleren',
  margin: 'Marge',
  padding: 'Opvulling',
  gap: 'Tussenruimte',
  rowGap: 'Rijafstand',
  columnGap: 'Kolomafstand',
  sideTop: 'Boven',
  sideRight: 'Rechts',
  sideBottom: 'Onder',
  sideLeft: 'Links',
  sameOnAllSides: 'Overal gelijk',
  custom: 'Aangepast…',
  customPx: 'Grootte in px',
  smaller: 'Kleiner',
  larger: 'Groter',
  pxValue: '{n} px',
  defaultValue: 'Standaard ({value})',
  channelText: 'Tekst',
  channelBackground: 'Achtergrond',
  channelBorder: 'Rand',
  channelFill: 'Vulling',
  channelStroke: 'Lijn',
  chooseColour: 'Kies een kleur voor {channel}',
  reset: 'Herstellen',
  colourPaper: 'Papier',
  colourSurface: 'Oppervlak',
  colourSoft: 'Zacht',
  colourLine: 'Lijn',
  colourLineStrong: 'Sterke lijn',
  colourSuccessBg: 'Achtergrond succes',
  colourWarningBg: 'Achtergrond waarschuwing',
  colourDangerBg: 'Achtergrond gevaar',
  colourSeries: 'Reeks {n}',
  formatUnsupported: 'Deze getalnotatie kan hier niet worden gewijzigd.',
  colourNone: 'Geen kleur',
  formatScalarNative: 'oorspronkelijke notatie',
  imageFit: 'Passend maken',
  imageRatio: 'Verhouding',
  fitContain: 'Volledig',
  fitCover: 'Vullen',
  ratioIntrinsic: 'Origineel',
  fitCoverUnsafe: 'Deze afbeelding kan niet worden bijgesneden.',
  imageNotReady: 'Passend maken kan veranderen zodra de afbeelding klaar is.',
};

const pl: Record<InspectCopyKey, string> = {
  fieldContent: 'Treść',
  inspectNodeParagraph: 'Akapit',
  inspectNodeHeading: 'Nagłówek',
  inspectNodeList: 'Lista',
  viewOnly: 'Możesz to wyświetlić, ale nie możesz tego zmienić.',
  undoFailed: 'Nie udało się cofnąć. Twoja zmiana nadal jest zapisana.',
  undoConflict: 'To zmieniło się po Twojej edycji, więc nie można tego tu cofnąć.',

  toggle: 'Zbadaj',
  hintTouch: 'Dotknij części, aby ją zaznaczyć.',
  addAnother: 'Dodaj kolejną',
  element: 'Element',
  region: 'Obszar',
  pickerLabel: 'Wybierz, co zaznaczyć',
  selectParent: 'Zaznacz element nadrzędny',
  mention: 'Wspomnij',
  remove: 'Usuń {label}',
  selectedCount: 'Zaznaczono: {n}',
  limit: 'Możesz wspomnieć naraz najwyżej {limit} części. Usuń jedną, aby dodać inną.',
  stale: 'Zmieniono. Zaznacz ponownie.',
  revoked: 'Już niedostępne',
  expired: 'Wygasło. Zaznacz ponownie.',
  details: 'Szczegóły',
  hideDetails: 'Ukryj szczegóły',
  source: 'Źródło',
  sourceHidden: 'Ukryte dla Twojego dostępu',
  sourceUnmapped: 'Niepowiązane ze źródłem',
  data: 'Dane',
  dataUnmapped: 'Źródło danych niepowiązane',
  picture: 'Obraz',
  pictureReplay: 'Z nowego renderowania tej wersji',
  pending: 'Wczytywanie…',
  targetsLabel: 'Części, które możesz wskazać',
  text: 'Tekst',
  colour: 'Kolor',
  spacing: 'Odstępy',
  numberFormat: 'Format liczb',
  mixed: 'Mieszane',
  apply: 'Zastosuj',
  cancel: 'Anuluj',
  applying: 'Zapisywanie…',
  saved: 'Zapisano w wersji roboczej',
  undo: 'Cofnij',
  change: '{before} → {after}',
  refused: 'Tego nie można zmienić bezpośrednio tutaj.',
  handoff: 'Zmień to w czacie',
  staleApply: 'Zmieniło się w trakcie edycji. Zaznacz ponownie.',
  failed: 'Nie udało się zapisać. Poprzednia wersja jest bez zmian.',
  hintBar: 'Kliknij część, aby o niej wspomnieć · Esc, aby wyjść',
  where: 'Gdzie',
  shows: 'Pokazuje',
  changesHere: 'Zmienia się tutaj',
  showSource: 'Pokaż, gdzie jest zdefiniowane',
  hideSource: 'Ukryj, gdzie jest zdefiniowane',
  cropUnavailable: 'Brak wycinka dla tego widoku',
  fromData: 'Pochodzi z danych, więc nie można tego nadpisać',
  noModel: 'Nie jest używany żaden model. Zapisuje nową wersję roboczą.',
  savedVersion: 'Zapisano w wersji roboczej {version}',
  checkBeforePublish: 'Sprawdź ponownie przed publikacją',
  staleSubject: '{subject}: zmieniono. Zaznacz ponownie.',
  staleGeneric: 'Zmieniono. Zaznacz ponownie.',
  shownAs: 'Pokaż jako',
  shownNumber: 'Liczba',
  shownPercent: 'Procent',
  decimals: 'Miejsca dziesiętne',
  units: 'Jednostka',
  unitKr: 'kr',
  unitThousandKr: 'tysiące kr',
  nowSuffix: '{value} obecnie',
  selectedToggle: 'Zaznaczono: {n}',
  grouping: 'Separator tysięcy',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Zaznacz ponownie, aby zmienić',
  colourInk: 'Atrament',
  colourMuted: 'Stonowany',
  colourBrand: 'Akcent',
  colourSuccess: 'Sukces',
  colourWarning: 'Ostrzeżenie',
  colourDanger: 'Zagrożenie',
  saveUncertain: 'To mogło zostać zapisane. Nie mogliśmy jeszcze tego potwierdzić.',
  checkAgain: 'Sprawdź ponownie',
  margin: 'Margines',
  padding: 'Dopełnienie',
  gap: 'Odstęp',
  rowGap: 'Odstęp między wierszami',
  columnGap: 'Odstęp między kolumnami',
  sideTop: 'Góra',
  sideRight: 'Prawo',
  sideBottom: 'Dół',
  sideLeft: 'Lewo',
  sameOnAllSides: 'Tak samo po wszystkich stronach',
  custom: 'Niestandardowy…',
  customPx: 'Rozmiar w px',
  smaller: 'Mniejszy',
  larger: 'Większy',
  pxValue: '{n} px',
  defaultValue: 'Domyślne ({value})',
  channelText: 'Tekst',
  channelBackground: 'Tło',
  channelBorder: 'Obramowanie',
  channelFill: 'Wypełnienie',
  channelStroke: 'Kontur',
  chooseColour: 'Wybierz kolor: {channel}',
  reset: 'Resetuj',
  colourPaper: 'Papier',
  colourSurface: 'Powierzchnia',
  colourSoft: 'Delikatny',
  colourLine: 'Linia',
  colourLineStrong: 'Wyraźna linia',
  colourSuccessBg: 'Tło sukcesu',
  colourWarningBg: 'Tło ostrzeżenia',
  colourDangerBg: 'Tło zagrożenia',
  colourSeries: 'Seria {n}',
  formatUnsupported: 'Tego formatu liczb nie można tu zmienić.',
  colourNone: 'Bez koloru',
  formatScalarNative: 'oryginalny format',
  imageFit: 'Dopasowanie',
  imageRatio: 'Proporcje',
  fitContain: 'Cały obraz',
  fitCover: 'Wypełnij',
  ratioIntrinsic: 'Oryginalne',
  fitCoverUnsafe: 'Tego obrazu nie można przyciąć.',
  imageNotReady: 'Dopasowanie można zmienić, gdy obraz będzie gotowy.',
};

const pt: Record<InspectCopyKey, string> = {
  fieldContent: 'Conteúdo',
  inspectNodeParagraph: 'Parágrafo',
  inspectNodeHeading: 'Título',
  inspectNodeList: 'Lista',
  viewOnly: 'Pode ver isto, mas não o pode alterar.',
  undoFailed: 'Não foi possível anular. A sua alteração continua guardada.',
  undoConflict: 'Isto mudou depois da sua edição, por isso não pode ser anulado aqui.',

  toggle: 'Inspecionar',
  hintTouch: 'Toque numa parte para a selecionar.',
  addAnother: 'Adicionar outra',
  element: 'Elemento',
  region: 'Área',
  pickerLabel: 'Escolha o que selecionar',
  selectParent: 'Selecionar o contentor',
  mention: 'Mencionar',
  remove: 'Remover {label}',
  selectedCount: '{n} selecionadas',
  limit: 'Pode mencionar até {limit} partes de cada vez. Remova uma para adicionar outra.',
  stale: 'Alterado. Selecione-o novamente.',
  revoked: 'Já não está disponível',
  expired: 'Expirou. Selecione-o novamente.',
  details: 'Detalhes',
  hideDetails: 'Ocultar detalhes',
  source: 'Origem',
  sourceHidden: 'Oculto para o seu nível de acesso',
  sourceUnmapped: 'Não associado à origem',
  data: 'Dados',
  dataUnmapped: 'Fonte de dados não associada',
  picture: 'Imagem',
  pictureReplay: 'De uma nova renderização desta versão',
  pending: 'A carregar…',
  targetsLabel: 'Partes que pode indicar',
  text: 'Texto',
  colour: 'Cor',
  spacing: 'Espaçamento',
  numberFormat: 'Formato de número',
  mixed: 'Misto',
  apply: 'Aplicar',
  cancel: 'Cancelar',
  applying: 'A guardar…',
  saved: 'Guardado no rascunho',
  undo: 'Anular',
  change: '{before} → {after}',
  refused: 'Isto não pode ser alterado diretamente aqui.',
  handoff: 'Alterar no chat',
  staleApply: 'Foi alterado enquanto editava. Selecione-o novamente.',
  failed: 'Não foi possível guardar. A versão anterior está inalterada.',
  hintBar: 'Clique numa parte para a mencionar · Esc para sair',
  where: 'Onde',
  shows: 'Mostra',
  changesHere: 'Altera aqui',
  showSource: 'Mostrar onde está definido',
  hideSource: 'Ocultar onde está definido',
  cropUnavailable: 'Sem recorte para esta vista',
  fromData: 'Vem dos dados, por isso não pode ser substituído',
  noModel: 'Não é usado nenhum modelo. Guarda um novo rascunho.',
  savedVersion: 'Guardado no rascunho {version}',
  checkBeforePublish: 'Verifique novamente antes de publicar',
  staleSubject: '{subject} foi alterado. Selecione novamente.',
  staleGeneric: 'Foi alterado. Selecione novamente.',
  shownAs: 'Mostrar como',
  shownNumber: 'Número',
  shownPercent: 'Percentagem',
  decimals: 'Casas decimais',
  units: 'Unidade',
  unitKr: 'kr',
  unitThousandKr: 'milhares de kr',
  nowSuffix: '{value} atual',
  selectedToggle: '{n} selecionadas',
  grouping: 'Separador de milhares',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Selecione-o novamente para o alterar',
  colourInk: 'Tinta',
  colourMuted: 'Atenuado',
  colourBrand: 'Destaque',
  colourSuccess: 'Sucesso',
  colourWarning: 'Aviso',
  colourDanger: 'Perigo',
  saveUncertain: 'Isto pode ter sido guardado. Ainda não conseguimos confirmar.',
  checkAgain: 'Verificar novamente',
  margin: 'Margem',
  padding: 'Preenchimento',
  gap: 'Espaçamento',
  rowGap: 'Espaçamento entre linhas',
  columnGap: 'Espaçamento entre colunas',
  sideTop: 'Topo',
  sideRight: 'Direita',
  sideBottom: 'Fundo',
  sideLeft: 'Esquerda',
  sameOnAllSides: 'Igual em todos os lados',
  custom: 'Personalizado…',
  customPx: 'Tamanho em px',
  smaller: 'Menor',
  larger: 'Maior',
  pxValue: '{n} px',
  defaultValue: 'Predefinição ({value})',
  channelText: 'Texto',
  channelBackground: 'Fundo',
  channelBorder: 'Contorno',
  channelFill: 'Preenchimento',
  channelStroke: 'Traço',
  chooseColour: 'Escolha a cor para {channel}',
  reset: 'Repor',
  colourPaper: 'Papel',
  colourSurface: 'Superfície',
  colourSoft: 'Suave',
  colourLine: 'Linha',
  colourLineStrong: 'Linha forte',
  colourSuccessBg: 'Fundo de sucesso',
  colourWarningBg: 'Fundo de aviso',
  colourDangerBg: 'Fundo de perigo',
  colourSeries: 'Série {n}',
  formatUnsupported: 'Este formato de número não pode ser alterado aqui.',
  colourNone: 'Sem cor',
  formatScalarNative: 'formato original',
  imageFit: 'Ajuste',
  imageRatio: 'Proporção',
  fitContain: 'Conter',
  fitCover: 'Preencher',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Esta imagem não pode ser recortada.',
  imageNotReady: 'O ajuste pode mudar quando a imagem estiver pronta.',
};

const ro: Record<InspectCopyKey, string> = {
  fieldContent: 'Conținut',
  inspectNodeParagraph: 'Paragraf',
  inspectNodeHeading: 'Titlu',
  inspectNodeList: 'Listă',
  viewOnly: 'Puteți vedea acest lucru, dar nu îl puteți modifica.',
  undoFailed: 'Anularea nu a reușit. Modificarea dvs. este în continuare salvată.',
  undoConflict: 'Acest lucru s-a schimbat după editarea dvs., așa că nu poate fi anulat aici.',

  toggle: 'Inspectează',
  hintTouch: 'Atinge o parte pentru a o selecta.',
  addAnother: 'Adaugă încă una',
  element: 'Element',
  region: 'Zonă',
  pickerLabel: 'Alege ce selectezi',
  selectParent: 'Selectează părintele',
  mention: 'Menționează',
  remove: 'Elimină {label}',
  selectedCount: '{n} selectate',
  limit: 'Poți menționa până la {limit} părți odată. Elimină una pentru a adăuga alta.',
  stale: 'S-a schimbat. Selectează din nou.',
  revoked: 'Nu mai este disponibil',
  expired: 'A expirat. Selectează din nou.',
  details: 'Detalii',
  hideDetails: 'Ascunde detaliile',
  source: 'Sursă',
  sourceHidden: 'Ascuns pentru nivelul tău de acces',
  sourceUnmapped: 'Nelegat de sursă',
  data: 'Date',
  dataUnmapped: 'Sursa de date nu este legată',
  picture: 'Imagine',
  pictureReplay: 'Dintr-o nouă randare a acestei versiuni',
  pending: 'Se încarcă…',
  targetsLabel: 'Părți pe care le poți indica',
  text: 'Text',
  colour: 'Culoare',
  spacing: 'Spațiere',
  numberFormat: 'Format numeric',
  mixed: 'Mixt',
  apply: 'Aplică',
  cancel: 'Anulează',
  applying: 'Se salvează…',
  saved: 'Salvat în ciornă',
  undo: 'Anulează modificarea',
  change: '{before} → {after}',
  refused: 'Acest lucru nu poate fi modificat direct aici.',
  handoff: 'Modifică în chat',
  staleApply: 'S-a schimbat în timp ce editai. Selectează din nou.',
  failed: 'Nu s-a putut salva. Versiunea anterioară este neschimbată.',
  hintBar: 'Fă clic pe o parte pentru a o menționa · Esc pentru a ieși',
  where: 'Unde',
  shows: 'Afișează',
  changesHere: 'Se modifică aici',
  showSource: 'Arată unde este definit',
  hideSource: 'Ascunde unde este definit',
  cropUnavailable: 'Nicio decupare pentru această vizualizare',
  fromData: 'Provine din date, deci nu poate fi suprascris',
  noModel: 'Nu se folosește niciun model. Salvează o ciornă nouă.',
  savedVersion: 'Salvat în ciorna {version}',
  checkBeforePublish: 'Verifică din nou înainte de publicare',
  staleSubject: '{subject} s-a schimbat. Selectează din nou.',
  staleGeneric: 'S-a schimbat. Selectează din nou.',
  shownAs: 'Afișează ca',
  shownNumber: 'Număr',
  shownPercent: 'Procent',
  decimals: 'Zecimale',
  units: 'Unitate',
  unitKr: 'kr',
  unitThousandKr: 'mii de kr',
  nowSuffix: '{value} acum',
  selectedToggle: '{n} selectate',
  grouping: 'Separator de mii',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Selectează din nou pentru a modifica',
  colourInk: 'Cerneală',
  colourMuted: 'Estompat',
  colourBrand: 'Accent',
  colourSuccess: 'Succes',
  colourWarning: 'Avertisment',
  colourDanger: 'Pericol',
  saveUncertain: 'Este posibil să se fi salvat. Încă nu am putut confirma.',
  checkAgain: 'Verifică din nou',
  margin: 'Margine',
  padding: 'Spațiere interioară',
  gap: 'Spațiu',
  rowGap: 'Spațiu între rânduri',
  columnGap: 'Spațiu între coloane',
  sideTop: 'Sus',
  sideRight: 'Dreapta',
  sideBottom: 'Jos',
  sideLeft: 'Stânga',
  sameOnAllSides: 'La fel pe toate laturile',
  custom: 'Personalizat…',
  customPx: 'Dimensiune în px',
  smaller: 'Mai mic',
  larger: 'Mai mare',
  pxValue: '{n} px',
  defaultValue: 'Implicit ({value})',
  channelText: 'Text',
  channelBackground: 'Fundal',
  channelBorder: 'Bordură',
  channelFill: 'Umplere',
  channelStroke: 'Contur',
  chooseColour: 'Alege culoarea pentru {channel}',
  reset: 'Resetează',
  colourPaper: 'Hârtie',
  colourSurface: 'Suprafață',
  colourSoft: 'Discret',
  colourLine: 'Linie',
  colourLineStrong: 'Linie pronunțată',
  colourSuccessBg: 'Fundal pentru succes',
  colourWarningBg: 'Fundal pentru avertisment',
  colourDangerBg: 'Fundal pentru pericol',
  colourSeries: 'Seria {n}',
  formatUnsupported: 'Acest format numeric nu poate fi modificat aici.',
  colourNone: 'Fără culoare',
  formatScalarNative: 'formatul original',
  imageFit: 'Încadrare',
  imageRatio: 'Proporție',
  fitContain: 'Întreagă',
  fitCover: 'Umple',
  ratioIntrinsic: 'Original',
  fitCoverUnsafe: 'Această imagine nu poate fi decupată.',
  imageNotReady: 'Încadrarea se poate schimba când imaginea este gata.',
};

const sk: Record<InspectCopyKey, string> = {
  fieldContent: 'Obsah',
  inspectNodeParagraph: 'Odsek',
  inspectNodeHeading: 'Nadpis',
  inspectNodeList: 'Zoznam',
  viewOnly: 'Toto si môžete pozrieť, ale nemôžete to zmeniť.',
  undoFailed: 'Vrátenie späť sa nepodarilo. Vaša zmena zostáva uložená.',
  undoConflict: 'Po vašej úprave sa to zmenilo, takže to tu nemožno vrátiť späť.',

  toggle: 'Skúmať',
  hintTouch: 'Ťuknutím na časť ju vyberiete.',
  addAnother: 'Pridať ďalší',
  element: 'Prvok',
  region: 'Oblasť',
  pickerLabel: 'Vyberte, čo označiť',
  selectParent: 'Vybrať nadradený prvok',
  mention: 'Spomenúť',
  remove: 'Odstrániť {label}',
  selectedCount: 'Vybrané: {n}',
  limit: 'Naraz môžete spomenúť najviac {limit} častí. Odstráňte jednu, ak chcete pridať ďalšiu.',
  stale: 'Zmenené. Vyberte znova.',
  revoked: 'Už nie je k dispozícii',
  expired: 'Platnosť vypršala. Vyberte znova.',
  details: 'Podrobnosti',
  hideDetails: 'Skryť podrobnosti',
  source: 'Zdroj',
  sourceHidden: 'Skryté pre vaše oprávnenia',
  sourceUnmapped: 'Neprepojené so zdrojom',
  data: 'Údaje',
  dataUnmapped: 'Zdroj údajov nie je prepojený',
  picture: 'Obrázok',
  pictureReplay: 'Z nového vykreslenia tejto verzie',
  pending: 'Načítava sa…',
  targetsLabel: 'Časti, na ktoré môžete ukázať',
  text: 'Text',
  colour: 'Farba',
  spacing: 'Medzery',
  numberFormat: 'Formát čísla',
  mixed: 'Zmiešané',
  apply: 'Použiť',
  cancel: 'Zrušiť',
  applying: 'Ukladá sa…',
  saved: 'Uložené do konceptu',
  undo: 'Vrátiť späť',
  change: '{before} → {after}',
  refused: 'Tu sa to nedá zmeniť priamo.',
  handoff: 'Zmeniť v chate',
  staleApply: 'Počas úprav sa to zmenilo. Vyberte znova.',
  failed: 'Uloženie zlyhalo. Predchádzajúca verzia zostáva bez zmeny.',
  hintBar: 'Kliknutím na časť ju spomeniete · Esc na ukončenie',
  where: 'Kde',
  shows: 'Zobrazuje',
  changesHere: 'Mení sa tu',
  showSource: 'Zobraziť, kde je definované',
  hideSource: 'Skryť, kde je definované',
  cropUnavailable: 'Pre toto zobrazenie nie je výrez',
  fromData: 'Pochádza z údajov, nedá sa to prepísať',
  noModel: 'Nepoužíva sa žiadny model. Uloží nový koncept.',
  savedVersion: 'Uložené do konceptu {version}',
  checkBeforePublish: 'Pred zverejnením znova skontrolujte',
  staleSubject: '{subject} sa zmenil(a). Vyberte znova.',
  staleGeneric: 'Zmenilo sa to. Vyberte znova.',
  shownAs: 'Zobraziť ako',
  shownNumber: 'Číslo',
  shownPercent: 'Percento',
  decimals: 'Desatinné miesta',
  units: 'Jednotka',
  unitKr: 'kr',
  unitThousandKr: 'tisíce kr',
  nowSuffix: '{value} teraz',
  selectedToggle: 'Vybrané: {n}',
  grouping: 'Oddeľovanie tisícov',
  groupOn: '1 000',
  groupOff: '1000',
  staleSelect: 'Na zmenu vyberte znova',
  colourInk: 'Atrament',
  colourMuted: 'Tlmená',
  colourBrand: 'Zvýraznenie',
  colourSuccess: 'Úspech',
  colourWarning: 'Upozornenie',
  colourDanger: 'Nebezpečenstvo',
  saveUncertain: 'Možno sa to uložilo. Zatiaľ sme to nemohli potvrdiť.',
  checkAgain: 'Skontrolovať znova',
  margin: 'Vonkajší okraj',
  padding: 'Vnútorný okraj',
  gap: 'Medzera',
  rowGap: 'Medzera medzi riadkami',
  columnGap: 'Medzera medzi stĺpcami',
  sideTop: 'Hore',
  sideRight: 'Vpravo',
  sideBottom: 'Dole',
  sideLeft: 'Vľavo',
  sameOnAllSides: 'Rovnako na všetkých stranách',
  custom: 'Vlastné…',
  customPx: 'Veľkosť v px',
  smaller: 'Menšie',
  larger: 'Väčšie',
  pxValue: '{n} px',
  defaultValue: 'Predvolené ({value})',
  channelText: 'Text',
  channelBackground: 'Pozadie',
  channelBorder: 'Okraj',
  channelFill: 'Výplň',
  channelStroke: 'Ťah',
  chooseColour: 'Vyberte farbu pre {channel}',
  reset: 'Obnoviť',
  colourPaper: 'Papier',
  colourSurface: 'Plocha',
  colourSoft: 'Jemná',
  colourLine: 'Čiara',
  colourLineStrong: 'Výrazná čiara',
  colourSuccessBg: 'Pozadie úspechu',
  colourWarningBg: 'Pozadie upozornenia',
  colourDangerBg: 'Pozadie nebezpečenstva',
  colourSeries: 'Rad {n}',
  formatUnsupported: 'Tento formát čísel tu nemožno zmeniť.',
  colourNone: 'Bez farby',
  formatScalarNative: 'pôvodný formát',
  imageFit: 'Prispôsobenie',
  imageRatio: 'Pomer',
  fitContain: 'Celý obrázok',
  fitCover: 'Vyplniť',
  ratioIntrinsic: 'Pôvodný',
  fitCoverUnsafe: 'Tento obrázok nemožno orezať.',
  imageNotReady: 'Prispôsobenie možno zmeniť, keď bude obrázok pripravený.',
};

const sl: Record<InspectCopyKey, string> = {
  fieldContent: 'Vsebina',
  inspectNodeParagraph: 'Odstavek',
  inspectNodeHeading: 'Naslov',
  inspectNodeList: 'Seznam',
  viewOnly: 'To si lahko ogledate, ne morete pa tega spremeniti.',
  undoFailed: 'Razveljavitev ni uspela. Vaša sprememba je še vedno shranjena.',
  undoConflict: 'To se je spremenilo po vašem urejanju, zato tega tukaj ni mogoče razveljaviti.',

  toggle: 'Preglej',
  hintTouch: 'Tapnite del, da ga izberete.',
  addAnother: 'Dodaj še enega',
  element: 'Element',
  region: 'Območje',
  pickerLabel: 'Izberite, kaj označiti',
  selectParent: 'Izberi nadrejeni',
  mention: 'Omeni',
  remove: 'Odstrani {label}',
  selectedCount: 'Izbrano: {n}',
  limit: 'Hkrati lahko omenite največ {limit} delov. Odstranite enega, da dodate drugega.',
  stale: 'Spremenjeno. Izberite znova.',
  revoked: 'Ni več na voljo',
  expired: 'Poteklo. Izberite znova.',
  details: 'Podrobnosti',
  hideDetails: 'Skrij podrobnosti',
  source: 'Vir',
  sourceHidden: 'Skrito za vaš dostop',
  sourceUnmapped: 'Ni povezano z virom',
  data: 'Podatki',
  dataUnmapped: 'Vir podatkov ni povezan',
  picture: 'Slika',
  pictureReplay: 'Iz novega upodabljanja te različice',
  pending: 'Nalaganje…',
  targetsLabel: 'Deli, na katere lahko pokažete',
  text: 'Besedilo',
  colour: 'Barva',
  spacing: 'Razmik',
  numberFormat: 'Oblika številk',
  mixed: 'Mešano',
  apply: 'Uporabi',
  cancel: 'Prekliči',
  applying: 'Shranjevanje…',
  saved: 'Shranjeno v osnutek',
  undo: 'Razveljavi',
  change: '{before} → {after}',
  refused: 'Tega tukaj ni mogoče spremeniti neposredno.',
  handoff: 'Spremenite to v klepetu',
  staleApply: 'Med urejanjem se je spremenilo. Izberite znova.',
  failed: 'Shranjevanje ni uspelo. Prejšnja različica je nespremenjena.',
  hintBar: 'Kliknite del, da ga omenite · Esc za izhod',
  where: 'Kje',
  shows: 'Prikazuje',
  changesHere: 'Spremeni se tukaj',
  showSource: 'Pokaži, kje je določeno',
  hideSource: 'Skrij, kje je določeno',
  cropUnavailable: 'Za ta pogled ni izreza',
  fromData: 'Prihaja iz podatkov, zato ga ni mogoče prepisati',
  noModel: 'Ne uporablja se noben model. Shrani nov osnutek.',
  savedVersion: 'Shranjeno v osnutek {version}',
  checkBeforePublish: 'Pred objavo ponovno preverite',
  staleSubject: '{subject}: spremenjeno. Izberite znova.',
  staleGeneric: 'Spremenilo se je. Izberite znova.',
  shownAs: 'Prikaži kot',
  shownNumber: 'Število',
  shownPercent: 'Odstotek',
  decimals: 'Decimalna mesta',
  units: 'Enota',
  unitKr: 'kr',
  unitThousandKr: 'tisoč kr',
  nowSuffix: '{value} zdaj',
  selectedToggle: 'Izbrano: {n}',
  grouping: 'Združevanje števk',
  groupOn: '1.000',
  groupOff: '1000',
  staleSelect: 'Izberite znova, da spremenite',
  colourInk: 'Črnilo',
  colourMuted: 'Zadušeno',
  colourBrand: 'Poudarek',
  colourSuccess: 'Uspeh',
  colourWarning: 'Opozorilo',
  colourDanger: 'Nevarnost',
  saveUncertain: 'To je morda shranjeno. Še nismo mogli potrditi.',
  checkAgain: 'Preveri znova',
  margin: 'Zunanji rob',
  padding: 'Notranji rob',
  gap: 'Razmik',
  rowGap: 'Razmik med vrsticami',
  columnGap: 'Razmik med stolpci',
  sideTop: 'Zgoraj',
  sideRight: 'Desno',
  sideBottom: 'Spodaj',
  sideLeft: 'Levo',
  sameOnAllSides: 'Enako na vseh straneh',
  custom: 'Po meri…',
  customPx: 'Velikost v px',
  smaller: 'Manjše',
  larger: 'Večje',
  pxValue: '{n} px',
  defaultValue: 'Privzeto ({value})',
  channelText: 'Besedilo',
  channelBackground: 'Ozadje',
  channelBorder: 'Obroba',
  channelFill: 'Polnilo',
  channelStroke: 'Poteza',
  chooseColour: 'Izberite barvo za {channel}',
  reset: 'Ponastavi',
  colourPaper: 'Papir',
  colourSurface: 'Površina',
  colourSoft: 'Mehka',
  colourLine: 'Črta',
  colourLineStrong: 'Izrazita črta',
  colourSuccessBg: 'Ozadje uspeha',
  colourWarningBg: 'Ozadje opozorila',
  colourDangerBg: 'Ozadje nevarnosti',
  colourSeries: 'Niz {n}',
  formatUnsupported: 'Te oblike zapisa števil tukaj ni mogoče spremeniti.',
  colourNone: 'Brez barve',
  formatScalarNative: 'izvirna oblika',
  imageFit: 'Prilagoditev',
  imageRatio: 'Razmerje',
  fitContain: 'Cela slika',
  fitCover: 'Zapolni',
  ratioIntrinsic: 'Izvirno',
  fitCoverUnsafe: 'Te slike ni mogoče obrezati.',
  imageNotReady: 'Prilagoditev lahko spremenite, ko bo slika pripravljena.',
};

export const inspectCopy: NamespaceCatalog<InspectCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default inspectCopy;
